/**
 * The unified production-record input's model (PROD-1279 stage-1 feedback,
 * plan-05 §4).
 *
 * ONE STEP PER MEALTIME, ONE FLAT ROW PER ITEM × AGE BAND. A step is one
 * production record (or, when no record was generated, the trucks of that
 * mealtime), and its rows are exactly the printed record's: an item served to
 * two bands is two rows whose legend names the band (`K-5 · 2 each`), never a
 * total row with sub-rows. Every one of the ten numeric columns is an INPUT on
 * every row, PRE-FILLED from whatever exists and tipped with its source:
 *
 *   - the SERVICE half comes from `/production-records/draft`
 *     (`ProductionFillMealtime`): the band rows, their four counts, the stored
 *     Ordered / Prepared / Received the school entered before, the serving
 *     reading ladder;
 *   - the TRANSPORT half comes from `/cafeteria/deliveries` per DELIVERY date,
 *     aggregated by `aggregateDeliveryRows` / `splitMilkDeliveries` so a
 *     group-split campus still counts one stack once; every band row of an item
 *     lists the SAME trucks (a truck line is joined per item, V5), and the
 *     item's Received total fans back to its shipment lines on receipt;
 *   - the DOCUMENT (`GET /:id/document`) supplies the plan facts (Ordered,
 *     Prepared, per-band transport lines), the chef's readings and the kitchen
 *     signature for the tips and the Kitchen card.
 *
 * MATCHING (unchanged, V6): a truck row → a record deliverable through the id
 * chain (`componentId` → document line → `bundleDeliverableId`/`menuItemId` →
 * `lineTargets`), then the line's own `deliverableName`, then the normalized
 * label. Claim once per mealtime; a line nobody recognises becomes a
 * TRANSPORT-ONLY row (one per item) on exactly one record, a mealtime with no
 * record becomes a record-less step.
 *
 * WEEKLY MILK JOINS THE RECORD (Abner, Sep 18). The campus's weekly milk drop
 * is one stack of cartons for the whole day, and the record's own milk rows
 * (`1% Milk`, `1% Choco Milk`) ARE that milk: every SKU of the drop runs
 * through the same join against EVERY record of the day (not claim-once), and
 * a SKU that matches renders on that record's milk rows — one row per band, as
 * for any item — with the TRUCK's Ordered / Prepared / Received in the
 * transport cells and the drop's readings in Kitchen °F / Received °F. Because
 * one SKU feeds several rows (both bands, breakfast and lunch), those rows
 * share ONE transport scope (`RecordRow.milk`): every row shows the same value,
 * editing any row edits the scope, and the drop's shipments are received once.
 * The id chain is what joins them: the record's SERVICE line carries the milk
 * component (`componentId` → `mi:<menuItemId>` → `lineTargets`), and where the
 * drop's lines carry no order identity (a weekly drop never does — Amana,
 * 2026-11-02) the fill's own `componentId` joins the SKU straight to the milk
 * it was ordered as, so the cartons sit in that milk's grade groups, never an
 * `All grades` block. A SKU no record recognises stays a transport-only row on
 * the first unsigned step, legend `Weekly milk delivery · not on this record`.
 *
 * A SEALED KIT IS ONE ROW (PROD-1441, Sheila 2026-10-08). A deliverable the
 * document shows packing two or more recipes (the record's `isKit` node) is
 * one row carrying every count and reading, exactly as the paper prints it;
 * the recipes inside ride on the row as `kit.components` (name, serving size,
 * recipe code) and the table renders them as count-less "in kit" sub-rows.
 * The kit's Prepared / Received come from the kit node of the document, which
 * levels the package's lines (146 kits, never 3 × 146), and from the package's
 * one root truck line. Rows are grouped the way the paper is: a GRADE-GROUP
 * block per band (the band is the group header, not legend text), then main
 * choices / shared sides and milk / condiments by the document's component
 * kinds, then the paper's kind order (`groupRecordRows`).
 *
 * TEMPERATURES ARE AN ITEM ATTRIBUTE (B4 / D-B4.2): every row of an item shares
 * one `tempScopeKey`; the flow renders the scope's value on each row and writes
 * the scope when any row is edited. Silent (no helper text); the three temp
 * headers carry a `title`. A weekly milk row's Kitchen °F / Received °F belong
 * to the DROP's scope instead (`MilkScope.dropKey`), across SKUs and steps.
 *
 * Pure — no React, no fetch. Unit-tested in
 * `client/test/lib/production-records/spreadsheet-model.test.ts`.
 */

import type { CafeteriaDeliveryWire, Mealtime } from '@/api/cafeteria'
import type {
  ProductionFillCarryIn,
  ProductionFillDeliverable,
  ProductionFillGradeRow,
  ProductionFillLineTarget,
  ProductionFillMealtime,
} from '@/api/cafeteria-production'
import type { DocumentLineNode, DocumentTempReading, ProductionRecordDocument } from '@/api/production-records'
import type { ScanSheetResponse } from '@/api/sheet-scan'
import type {
  AggregatedDeliveryPart,
  AggregatedDeliveryRow,
  MilkReceiptGroup,
} from '@/lib/cafeteria/aggregate-deliveries'
import {
  distributeReceivedTotal,
  isMilkDeliveryLine,
  TEMP_CLASS_PREFILL_F,
  tempCheckRequired,
} from '@/lib/cafeteria/aggregate-deliveries'
import {
  deliveryTempKey,
  facilityTempKey,
  formatServeHeaderDateLabel,
  productionFillRowKey,
  TRANSPORT_ROW_PREFIX,
} from '@/lib/cafeteria/format'
import type { CountField, ProductionCounts } from '@/lib/cafeteria/production-counts'
import { deriveProductionCounts } from '@/lib/cafeteria/production-counts'
import { formatTemperatureF } from '@/lib/cafeteria/temperature-edit'
import { MEALTIME_LABELS } from '@/lib/mealtime'
import { kindRank, rowKindRank } from '@/lib/production-records/row-order'
import type { ScanCellFlag, ScanPartialSummary, ServiceCountPatch } from '@/lib/sheet-scan/apply-result'
import { buildPhotoWarnings } from '@/lib/sheet-scan/apply-result'
import type { ScanRowWire } from '@/lib/sheet-scan/build-request'

export type TemperatureClass = 'HOT' | 'CHILLED' | 'AMBIENT'

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

/** The ten numeric columns, in the printed order, by group. */
export const TRANSPORT_COUNT_COLUMNS = ['ordered', 'prepared', 'received'] as const
export const SERVICE_COUNT_COLUMNS = ['served', 'reimbursable', 'nonReimbursable', 'leftover'] as const
export const TEMP_COLUMNS = ['kitchenTemp', 'receivedTemp', 'servingTemp'] as const

export type TransportCountColumn = (typeof TRANSPORT_COUNT_COLUMNS)[number]
export type ServiceCountColumn = (typeof SERVICE_COUNT_COLUMNS)[number]
export type CountColumn = TransportCountColumn | ServiceCountColumn
export type TempColumn = (typeof TEMP_COLUMNS)[number]
export type RecordColumn = CountColumn | TempColumn

export const RECORD_COLUMNS: readonly RecordColumn[] = [
  ...TRANSPORT_COUNT_COLUMNS,
  ...SERVICE_COUNT_COLUMNS,
  ...TEMP_COLUMNS,
]

export function isTempColumn(column: RecordColumn): column is TempColumn {
  return (TEMP_COLUMNS as readonly string[]).includes(column)
}

export function isTransportCountColumn(column: RecordColumn): column is TransportCountColumn {
  return (TRANSPORT_COUNT_COLUMNS as readonly string[]).includes(column)
}

// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------

/** One truck's meal rows for one mealtime. `pending` = a shipment on it still needs receipt. */
export interface SpreadsheetTransportGroup {
  /** yyyy-mm-dd DELIVERY date — the axis `arrivedAt` keys on. */
  deliveryDate: string
  /** A shipment on this truck still awaits receipt. NOT an editability flag. */
  pending: boolean
  /** When and by whom the truck was received, for the `Confirmed on …` tip. */
  receivedAt: string | null
  receiverName: string | null
  mealtime: Mealtime
  rows: readonly AggregatedDeliveryRow[]
}

/** One truck's weekly milk drop: per-SKU counts under ONE chilled reading. */
export interface SpreadsheetMilkGroup {
  deliveryDate: string
  pending: boolean
  receivedAt: string | null
  receiverName: string | null
  group: MilkReceiptGroup
}

/** One shipment feeding the day — the Delivery card and the receipt plan read these. */
export interface TruckSummary {
  shipmentId: string
  deliveryDate: string
  status: CafeteriaDeliveryWire['status']
  receivedAt: string | null
  receiverName: string | null
}

export interface SpreadsheetInput {
  mealtimes: readonly ProductionFillMealtime[]
  /** Record id → its compliance document. Missing while the read is in flight. */
  documents: ReadonlyMap<string, ProductionRecordDocument>
  transport: readonly SpreadsheetTransportGroup[]
  milk: readonly SpreadsheetMilkGroup[]
  /**
   * Every shipment behind `transport` and `milk`, by id. Optional: a shipment
   * a row stands on but nobody listed here is summarised from its group's
   * facts, so a caller (or a fixture) that only knows the rows still gets a
   * complete step.
   */
  shipments?: readonly TruckSummary[]
}

// ---------------------------------------------------------------------------
// Output
// ---------------------------------------------------------------------------

/**
 * Whose number a pre-filled cell holds. Structurally the `CafeteriaModeTip`
 * props of the same kinds, so the flow renders one with a spread.
 */
export type PrefillTip =
  | { kind: 'kitchen'; label: string; loggedAt: string | null; loggedByName: string | null }
  | { kind: 'logged'; label: string; loggedAt: string | null; loggedByName: string | null }
  | { kind: 'confirmed'; at: string | null; byName: string | null; dateLabel: string }
  /** `saved`: the school's own number, saved on the record earlier; else Ordo's (the order, the delivery plan). */
  | { kind: 'source'; label: string; saved?: true }

/**
 * True when a pre-filled value is ORDO'S ASSUMPTION rather than anyone's entry
 * (PROD-1511): the kitchen's reading standing in for the school's, or a number
 * taken from the order or the delivery plan. A cafeteria log, a confirmed
 * receipt and a number the school saved are entries. The phone draws an
 * assumption dashed until someone types or scans over it.
 */
export function prefillIsAssumption(tip: PrefillTip | null): boolean {
  if (tip === null) {
    return false
  }
  return tip.kind === 'kitchen' || (tip.kind === 'source' && tip.saved !== true)
}

export interface PrefillValue {
  value: number | null
  tip: PrefillTip | null
}

export interface PrefillTemp extends PrefillValue {
  /** False on a shelf-stable row: no reading is taken at all (`N/A — room temp`). */
  offered: boolean
  /**
   * The value is a `ShipmentEvent`'s (the chef's reading, an earlier receipt).
   * A truck-less item never re-saves such a prefill as a RECORD-scoped log
   * while it is untouched: the document already shows the event, and a second
   * copy of the same reading would print twice.
   */
  fromShipmentEvent: boolean
}

export interface RecordRowPrefill {
  ordered: PrefillValue
  prepared: PrefillValue
  received: PrefillValue
  served: PrefillValue
  reimbursable: PrefillValue
  nonReimbursable: PrefillValue
  leftover: PrefillValue
  kitchenTemp: PrefillTemp
  receivedTemp: PrefillTemp
  servingTemp: PrefillTemp
}

/** One shipment line an item stands on, with the truck facts it arrived under. */
export interface TruckLineRef extends AggregatedDeliveryPart {
  deliveryDate: string
  /** The aggregated row the line was folded into. */
  rowKey: string
  pending: boolean
  receivedAt: string | null
  receiverName: string | null
  /** The line is part of the campus's WEEKLY MILK drop (`SpreadsheetMilkGroup`), not a meal truck. */
  weeklyMilk: boolean
}

/**
 * A row fed by the campus's WEEKLY MILK drop. The drop is one physical stack
 * of cartons counted and read ONCE however many records and bands it feeds
 * (the shipped Transport logs task's `MilkReceiptGroup`), so a milk row's
 * transport cells and drop readings are shared beyond the row:
 *   - `scopeKey` is the SKU: one Ordered / Prepared / Received across every
 *     row the SKU feeds, on every step (both bands, breakfast AND lunch);
 *     editing any of them edits the scope;
 *   - `dropKey` is the drop: one Kitchen °F / Received °F for every SKU of the
 *     drop, on every step, so a receipt posted from any step carries a reading
 *     for every line of the drop's shipments.
 * The Serving °F stays the record row's own (`tempScopeKey`).
 */
export interface MilkScope {
  /** `milk::${deliveryDate}::${vendorLocationId}` — also the drop's temperature scope, and (prefixed) its `temps` store key. */
  dropKey: string
  /** `${dropKey}::${skuRowKey}` — also the cross-step cell-id prefix of the SKU's transport counts. */
  scopeKey: string
  /**
   * `transport::${scopeKey}` — the `edits[step].rows` key the SKU's counts live
   * under on every step that has a row of it. `transport::`-prefixed so the
   * draft overlay carries it (`isTransportRowKey`).
   */
  storeKey: string
}

/** The service half of a row: the production-record band it saves to. */
export interface RecordRowService {
  productionRecordId: string
  deliverableKey: string
  deliverable: ProductionFillDeliverable
  /**
   * The band, and the `edits.rows` key its four counts live under. Null on a
   * deliverable the fill sent with NO grade rows: its serving reading still
   * saves, its counts have no bucket to land in.
   */
  gradeRow: ProductionFillGradeRow | null
  rowKey: string | null
  planned: number
}

/** One flat row: an item × one band, an item with no record, or one milk SKU. */
export interface RecordRow {
  /** Unique within the step; the `edits.rows` key for the row's own counts. */
  key: string
  /**
   * Servings per truck package (`truckUnitScale`): the row's Received reads
   * the truck's packages times this, and a typed Received goes back to the
   * truck divided by it. 1 (or absent) when a package is one serving.
   */
  truckUnitScale?: number
  /** The item the row belongs to; every band row of one item shares it. */
  itemKey: string
  label: string
  /**
   * The legend line under the name: `K-5 · 2 each`, `All grades · 1 Carton`,
   * vendor + ages; a weekly milk row appends `Weekly milk delivery`
   * (`1% Milk · 1 Carton · Weekly milk delivery`), an unjoined SKU reads
   * `Weekly milk delivery · not on this record`.
   */
  legend: string
  /** The band alone, for accessible names (`Chicken bowl K-5 served`). Null on band-less rows. */
  band: string | null
  temperatureClass: TemperatureClass | null
  /** Unique on screen: every row of one item shares it (D-B4.2). */
  tempScopeKey: string
  /** The suffix the scope's temperatures live under in `edits.temps` (`facility::` / `delivery::` + this; the serving reading is the bare key). */
  tempStoreKey: string
  service: RecordRowService | null
  /** Every truck line the ITEM stands on. Empty on a truck-less item. */
  trucks: TruckLineRef[]
  /** Save identity for the four service counts and the serving reading on a transport-only row, when the document knows the item. */
  fallbackIdentity: { bundleDeliverableId: string | null; menuItemId: string | null } | null
  /** Set on a row fed by the weekly milk drop: its transport cells and drop readings are scoped beyond the row. */
  milk: MilkScope | null
  /** PROD-1441: the recipes inside a sealed kit, rendered as count-less sub-rows under this row; null on a single-recipe row. */
  kit: RecordRowKit | null
  /** PROD-1441: the paper's section the row prints in (main choices / shared sides and milk / condiments). */
  section: RecordRowSection
  /** A meal sold only on the Field Trip menu: the trip's milk lines file under it (`fieldTripAnchorRow`). Optional: absent reads as no. */
  fieldTripMeal?: boolean
  /** PROD-1441: the paper's kind order inside a section (main, grain, vegetable, fruit, milk, side, condiment); unknown last. */
  sortRank: number
  prefill: RecordRowPrefill
}

export type RecordRowSection = 'MAIN' | 'SIDE' | 'CONDIMENT'

/** One recipe inside a sealed kit, as the document's kit sub-row describes it. */
export interface KitComponent {
  componentId: string
  name: string
  /** The paper's serving for the sub-row: the recipe's serving, else the line's label. */
  servingSize: string | null
  recipeCode: string | null
  kindLabel: string | null
  componentKind: string | null
}

export interface RecordRowKit {
  components: KitComponent[]
}

export interface RecordStep {
  /** The record id, or `transport::<MEALTIME>` for a record-less mealtime. */
  key: string
  /** Null only on the milk-only step of a day with no other trucks and no records. */
  mealtime: Mealtime | null
  title: string
  productionRecordId: string | null
  status: string | null
  /** The record is SUBMITTED or AMENDED. Always false on a record-less step. */
  signed: boolean
  rows: RecordRow[]
  trucks: TruckSummary[]
  /** The Kitchen card: the chef's signature on the record, from the document. */
  kitchen: { signatureName: string | null; signedAt: string | null }
  /** The record's own service signature, from the document (a step signed elsewhere). */
  signature: { name: string | null; signedAt: string | null }
  /** The fill mealtime behind a record step (write-ins, the save chain). */
  fill: ProductionFillMealtime | null
}

/** A record's status once a school signature closed it. */
export function isSignedStatus(status: string | null | undefined): boolean {
  return status === 'SUBMITTED' || status === 'AMENDED'
}

// ---------------------------------------------------------------------------
// Cell identity (touched set, flag keys, provenance)
// ---------------------------------------------------------------------------

/** A count cell's identity on screen. */
export function countCellId(stepKey: string, rowKey: string, column: CountColumn): string {
  return `${stepKey}::${rowKey}::${column}`
}

/** A temperature cell's identity on screen: the SCOPE, not the row. */
export function tempCellId(scopeKey: string, column: TempColumn): string {
  return `${scopeKey}::${column}`
}

/**
 * A count cell's identity on screen, per row. A weekly milk row's Ordered and
 * Prepared answer to the SKU's cross-step scope instead, so a touch on the
 * lunch row is a touch on the breakfast row of the same SKU. Its Ordered and
 * its Received (the Started with) are the meal's own (`isMilkMealCount`), so
 * they stay the row's.
 */
export function countCellIdFor(stepKey: string, row: RecordRow, column: CountColumn): string {
  if (row.milk && isTransportCountColumn(column) && !isMilkMealCount(row, column)) {
    return `${row.milk.scopeKey}::${column}`
  }
  return countCellId(stepKey, row.key, column)
}

/** The scope a row's temperature column belongs to: the DROP's for a weekly milk row's Kitchen °F / Received °F, else the item's. */
export function tempScopeKeyFor(row: RecordRow, column: TempColumn): string {
  return column !== 'servingTemp' && row.milk ? row.milk.dropKey : row.tempScopeKey
}

/** A temperature cell's identity on screen, per row (see `tempScopeKeyFor`). */
export function tempCellIdFor(row: RecordRow, column: TempColumn): string {
  return tempCellId(tempScopeKeyFor(row, column), column)
}

/**
 * The `edits[step].temps` key a row's temperature column lives under:
 * `facility::` / `delivery::` + the item's store key (the drop's on a weekly
 * milk row), the bare store key for the serving reading.
 */
export function tempStoreKeyFor(row: RecordRow, column: TempColumn): string {
  switch (column) {
    case 'kitchenTemp':
      return facilityTempKey(row.milk?.dropKey ?? row.tempStoreKey)
    case 'receivedTemp':
      return deliveryTempKey(row.milk?.dropKey ?? row.tempStoreKey)
    case 'servingTemp':
      return row.tempStoreKey
  }
}

/** The `edits[step].rows` key a row's three transport counts live under: the SKU's store row on a weekly milk row, else the row's own. */
export function transportStoreKeyFor(row: RecordRow): string {
  return row.milk?.storeKey ?? row.key
}

/** The cell id a row's column answers to. */
export function cellIdFor(stepKey: string, row: RecordRow, column: RecordColumn): string {
  return isTempColumn(column) ? tempCellIdFor(row, column) : countCellIdFor(stepKey, row, column)
}

// ---------------------------------------------------------------------------
// Milk: what each meal started with
// ---------------------------------------------------------------------------

/**
 * A row whose Prepared box (the `received` column) is what the MEAL had on
 * hand, saved on its own band (2026-11-04, Planned → Prepared → Served →
 * Leftover): every row on a record band. Served and Leftover interlock
 * against it (Leftover = Prepared − Served). On a food row it defaults to the
 * delivery's count, and the truck's receipt is `truckReceiptFor` (never above
 * what the truck brought); on a milk row (`isMilkStartRow`) it carries the
 * week's shelf instead (`milkStartDefault`) and never moves the receipt.
 */
export function isPreparedRow(row: Pick<RecordRow, 'service'>): boolean {
  return row.service?.gradeRow != null
}

/**
 * What a food item's Prepared total posts as the truck's receipt, in
 * packages: never more than the truck brought (`plannedPackages`). A shortage
 * (Prepared 138 against 140 shipped) records the short receipt; stock already
 * at the school above the truck's count never inflates it.
 */
export function truckReceiptFor(preparedPackages: number, plannedPackages: number): number {
  return Math.max(0, Math.min(preparedPackages, plannedPackages))
}

/**
 * A milk row on a record band, whose Prepared box is the milk the school had
 * on hand for THAT MEAL, not the truck's count (Amana, 2026-11-02). Milk is
 * one stock for the week: an earlier meal's leftovers are a later meal's
 * start, and the shelf usually holds more than any truck brought. So the box
 * belongs to the one meal (saved on its own band), Served and Leftover
 * interlock against it, and the truck's receipt keeps the truck's own count.
 * Every standalone milk item counts this way, weekly drop or not (Amana,
 * 2026-11-03: a breakfast with no drop started with 290 chocolate against a
 * 50 order). Food never carries: a fresh meal is allocated to one delivery.
 */
export function isMilkStartRow(row: Pick<RecordRow, 'milk' | 'service'>): boolean {
  return row.service?.gradeRow != null && (row.milk !== null || row.service.deliverable.isMilk === true)
}

/** The field-trip line's name on every surface (2026-11-04). */
export const FIELD_TRIP_LABEL = 'Sent on field trip'

/**
 * A milk band row carries a field-trip line: how many cartons of this milk
 * the school sent out with field-trip meals. The school does not know ahead
 * of time which milk the trip takes, so every milk band offers the line,
 * planned on the Field Trip menu or not.
 */
export function hasFieldTripLine(row: Pick<RecordRow, 'milk' | 'service'>): boolean {
  return isMilkStartRow(row)
}

/**
 * The row a step's field-trip milk lines file under (2026-11-04: "the milk
 * should sort with the field trip menu item itself"): the first meal sold
 * only on the Field Trip menu, a main choice first. Null when the step has
 * none — each milk row then keeps its own line. Pure.
 */
export function fieldTripAnchorRow(rows: readonly RecordRow[]): RecordRow | null {
  const meals = rows.filter((row) => row.fieldTripMeal === true)
  return meals.find((row) => row.section === 'MAIN') ?? meals[0] ?? null
}

/** A milk's field-trip line title under the field-trip meal: `<milk> sent on field trip`. */
export function fieldTripMilkLabel(row: Pick<RecordRow, 'label' | 'band'>): string {
  return `${row.band ? `${row.label} ${row.band}` : row.label} ${FIELD_TRIP_LABEL.toLowerCase()}`
}

/** The milk ordered on the Field Trip menu for this band (kept out of Planned). */
export function fieldTripPlannedFor(row: Pick<RecordRow, 'service'>): number {
  return row.service?.gradeRow?.fieldTripPlanned ?? 0
}

/** The field-trip line's legend: the trip's planned milk when the Field Trip menu ordered any. */
export function fieldTripLegend(planned: number): string {
  return planned > 0 ? `${planned} planned on field-trip meals` : 'Cartons that left with field-trip meals'
}

/**
 * What the cafeteria's Served and Leftover add up to on a milk band: the
 * meal's Prepared less the cartons sent out on the field trip (Leftover =
 * Prepared − Served − Field trip). Never below 0.
 */
export function onHandAfterFieldTrip(prepared: number, fieldTrip: number | null | undefined): number {
  return Math.max(0, prepared - Math.max(0, fieldTrip ?? 0))
}

/**
 * A milk start row's count that belongs to the ONE meal and saves on its band:
 * Prepared (the on-hand count) and the read-only Planned. Both answer to the
 * meal's own cell, never the SKU scope.
 */
export function isMilkMealCount(row: Pick<RecordRow, 'milk' | 'service'>, column: TransportCountColumn): boolean {
  return (column === 'received' || column === 'ordered') && isMilkStartRow(row)
}

/**
 * The milk a row holds, across meals: the fill's component, else the SKU
 * scope. A carry is strictly per item: the same milk offered again, never by
 * kind, never across milks.
 */
function milkIdentity(row: RecordRow): string | null {
  return row.service?.deliverable.componentId ?? row.milk?.scopeKey ?? null
}

/** A milk row's default Prepared, and where it came from. */
export interface MilkStart {
  value: number | null
  /**
   * `previous`: an earlier meal's Leftover of the same milk today; `carry`: an
   * earlier day's this calendar week (the server's `carryIn`) plus what trucks
   * brought since; `delivery`: the truck's count.
   */
  source: 'previous' | 'carry' | 'delivery'
  /** The tip's wording (`Breakfast leftover: 141`, `Mon lunch leftover 40 + Wed delivery 250`); null with no value. */
  label: string | null
  /** The earlier meal's title (`Breakfast`), on a `previous` start. */
  fromTitle: string | null
}

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const

/** `Tue` for `2026-11-03`: plain calendar math on the date string, no timezone. */
export function weekdayShort(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number) as [number, number, number]
  return WEEKDAY_SHORT[new Date(Date.UTC(year, month - 1, day)).getUTCDay()]!
}

/**
 * The cross-day carry of one milk deliverable (`carryIn` on the fill wire) as
 * a start: the earlier meal's Leftover plus every delivery of that milk since.
 * Null when the server sent none: an older server, or nothing counted earlier
 * this calendar week (the server's `canCarryMilk` owns that rule).
 */
export function milkCarryStart(carryIn: ProductionFillCarryIn | null | undefined): MilkStart | null {
  if (!carryIn) {
    return null
  }
  const received = carryIn.receivedSince.reduce((sum, entry) => sum + entry.quantity, 0)
  const mealtime = (MEALTIME_LABELS[carryIn.fromMealtime] ?? carryIn.fromMealtime).toLowerCase()
  const from = `${weekdayShort(carryIn.fromServiceDate)} ${mealtime} leftover`
  const label =
    carryIn.receivedSince.length === 0
      ? `${from}: ${carryIn.value}`
      : [
          `${from} ${carryIn.value}`,
          ...carryIn.receivedSince.map((entry) => `${weekdayShort(entry.deliveryDate)} delivery ${entry.quantity}`),
        ].join(' + ')
  return { value: carryIn.value + received, source: 'carry', label, fromTitle: null }
}

/**
 * The Prepared a milk row opens on before anyone types one, strictly per item:
 *   1. the LEFTOVER of the same milk at the latest earlier meal TODAY that
 *      offered and counted it — on the same band, or on the one row each meal
 *      counts it on when the bands differ (Nov 2: lunch chocolate starts at
 *      breakfast's 141 left). A meal that does not offer the milk has no row
 *      and is skipped, and so is one with nothing entered yet (Served and
 *      Leftover both empty or 0);
 *   2. else the server's cross-day carry for the deliverable (`carryIn`: an
 *      earlier day of this calendar week, plus deliveries since), when this
 *      meal counts the milk on one row;
 *   3. else the delivery's count (the first meal of the week: the weekly drop).
 * Read live (`valueOf`), so an edit on the earlier meal moves this default.
 */
export function milkStartDefault(
  steps: readonly RecordStep[],
  stepKey: string,
  row: RecordRow,
  valueOf: (step: RecordStep, row: RecordRow, column: ServiceCountColumn) => number | null,
): MilkStart {
  const truck = row.prefill.received.value
  const delivery: MilkStart = {
    value: truck,
    source: 'delivery',
    label: truck === null ? null : `${WEEKLY_MILK_TIP}: ${truck}`,
    fromTitle: null,
  }
  const identity = milkIdentity(row)
  const index = steps.findIndex((step) => step.key === stepKey)
  if (identity === null || index < 0) {
    return delivery
  }
  const sameMilk = (other: RecordRow): boolean => isMilkStartRow(other) && milkIdentity(other) === identity
  const oneRowHere = steps[index]!.rows.filter(sameMilk).length === 1
  for (let i = index - 1; i >= 0; i -= 1) {
    const earlier = steps[i]!
    const candidates = earlier.rows.filter(sameMilk)
    // Same band first. Milk is ONE shelf, though, and a meal's band is the
    // menu's portion band, not the stock's: Amana's breakfast is stamped K-12
    // and its lunch K-8 (Nov 4), so a band-exact match never carried
    // breakfast's leftover to lunch. When both meals count the milk on a
    // single row, that row is the earlier count whatever its band.
    const match =
      candidates.find((other) => other.band === row.band) ??
      (oneRowHere && candidates.length === 1 ? candidates[0] : undefined)
    if (!match) {
      continue
    }
    const served = valueOf(earlier, match, 'served') ?? 0
    const leftover = valueOf(earlier, match, 'leftover') ?? 0
    if (served > 0 || leftover > 0) {
      return {
        value: leftover,
        source: 'previous',
        label: `${earlier.title} leftover: ${leftover}`,
        fromTitle: earlier.title,
      }
    }
  }
  const carry = oneRowHere ? milkCarryStart(row.service?.deliverable.carryIn) : null
  return carry ?? delivery
}

/**
 * A row's four counts once its Prepared changes. The count the school typed
 * (Served first, else Leftover) stands and the other re-derives against the
 * new Prepared (Leftover = Prepared − Served); with neither typed, a Served
 * already on the row drives; with no Served at all nothing moves.
 */
export function rederiveCountsForPrepared(input: {
  current: ProductionCounts
  prepared: number | null
  manual: ReadonlySet<CountField>
}): ProductionCounts {
  const { current, manual } = input
  const driver: CountField | null = manual.has('served')
    ? 'served'
    : manual.has('leftover')
      ? 'leftover'
      : current.served > 0
        ? 'served'
        : null
  if (driver === null) {
    return { ...current }
  }
  return deriveProductionCounts({
    current,
    field: driver,
    value: current[driver],
    planned: input.prepared ?? 0,
    manual,
  }).counts
}

/**
 * What a milk row's Prepared box shows and the tip beside it: the meal's own
 * number when the school entered one (typed, or saved earlier), with the
 * "saved" tip; else the default (`milkStartDefault`) with its source. A cleared
 * box (`own` null) and a never-entered one (`own` undefined) both fall back to
 * the default, so the carry is a suggestion she can always type over.
 */
export function milkStartCell(own: number | null | undefined, start: MilkStart): PrefillValue {
  if (own != null) {
    return { value: own, tip: savedTip(own) }
  }
  if (start.value === null) {
    return EMPTY_VALUE
  }
  return { value: start.value, tip: start.label === null ? null : sourceTip(start.label) }
}

// ---------------------------------------------------------------------------
// Matching helpers
// ---------------------------------------------------------------------------

/**
 * The join key for a name-based match: case-folded, punctuation-stripped,
 * whitespace-collapsed. Deliberately conservative — it must never merge two
 * genuinely different items, so nothing is truncated and no word is dropped.
 */
export function normalizeLabel(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[‘’“”]/g, "'")
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

type ReadingSource = 'SHIPMENT_EVENT' | 'PRODUCTION_RECORD'

/**
 * Where a document reading came from. `source` is the print lane's additive
 * mirror field (plan-05 §3.5); it is read structurally so this module compiles
 * whether or not that mirror has landed, and a reading without it is treated
 * as a `ShipmentEvent`'s (the only source before record-scoped logs existed).
 */
function readingSource(reading: DocumentTempReading): ReadingSource {
  const source = (reading as { source?: unknown }).source
  return source === 'PRODUCTION_RECORD' ? 'PRODUCTION_RECORD' : 'SHIPMENT_EVENT'
}

/** Read-only facts one document line (or reading) contributes to an item. */
interface DocumentFacts {
  ordered: number | null
  prepared: number | null
  kitchenTempF: number | null
  kitchenLoggedAt: string | null
  kitchenSource: ReadingSource | null
  deliveryTempF: number | null
  deliveryLoggedAt: string | null
  deliverySource: ReadingSource | null
}

const EMPTY_FACTS: DocumentFacts = {
  ordered: null,
  prepared: null,
  kitchenTempF: null,
  kitchenLoggedAt: null,
  kitchenSource: null,
  deliveryTempF: null,
  deliveryLoggedAt: null,
  deliverySource: null,
}

function addNullable(a: number | null, b: number | null): number | null {
  if (a === null) {
    return b
  }
  if (b === null) {
    return a
  }
  return a + b
}

function mergeFacts(a: DocumentFacts, b: DocumentFacts): DocumentFacts {
  return {
    ordered: addNullable(a.ordered, b.ordered),
    prepared: addNullable(a.prepared, b.prepared),
    kitchenTempF: a.kitchenTempF ?? b.kitchenTempF,
    kitchenLoggedAt: a.kitchenLoggedAt ?? b.kitchenLoggedAt,
    kitchenSource: a.kitchenSource ?? b.kitchenSource,
    deliveryTempF: a.deliveryTempF ?? b.deliveryTempF,
    deliveryLoggedAt: a.deliveryLoggedAt ?? b.deliveryLoggedAt,
    deliverySource: a.deliverySource ?? b.deliverySource,
  }
}

/** The identity keys a document line (or a reading) can be looked up by. */
function identityKeys(node: {
  bundleDeliverableId: string | null
  menuItemId: string | null
  componentId?: string | null
}): string[] {
  const keys: string[] = []
  if (node.bundleDeliverableId) {
    keys.push(`bd:${node.bundleDeliverableId}`)
  }
  if (node.menuItemId) {
    keys.push(`mi:${node.menuItemId}`)
  }
  if (node.componentId) {
    keys.push(`co:${node.componentId}`)
  }
  return keys
}

function readingValue(reading: DocumentTempReading): number | null {
  const value = Number(reading.value)
  return Number.isFinite(value) ? value : null
}

/**
 * Every line of both logs, packaging skipped, each line once — a sealed kit's
 * component sub-rows (`children`, PROD-1441) right after their kit, so the
 * identity indexes know the recipes inside a package. Plan FACTS never read
 * children: `transportLines` is top-level only.
 */
function documentLines(doc: ProductionRecordDocument): DocumentLineNode[] {
  const seen = new Set<DocumentLineNode>()
  const out: DocumentLineNode[] = []
  const visit = (line: DocumentLineNode): void => {
    if (line.isPackaging || seen.has(line)) {
      return
    }
    seen.add(line)
    out.push(line)
    for (const child of line.children) {
      visit(child)
    }
  }
  for (const line of [
    ...doc.transport.groups.flatMap((group) => group.lines),
    ...doc.service.groups.flatMap((group) => group.lines),
  ]) {
    visit(line)
  }
  return out
}

/**
 * The TRANSPORT log's lines, packaging skipped: the plan facts (a line's
 * Ordered, Prepared and Received) live here. The service log's lines carry the
 * school's OWN Ordered / Prepared / Received (which the fill wire already
 * delivers as stored per-band values) or null, never a plan fact, and reading
 * them beside the transport lines counted a package twice: one whole-campus
 * line of 100 beside an entered 60 handed the band nobody entered a share of
 * 160.
 */
function transportLines(doc: ProductionRecordDocument): DocumentLineNode[] {
  return doc.transport.groups.flatMap((group) => group.lines).filter((line) => !line.isPackaging)
}

/**
 * Index one record's document by every identity a row could match on. ONE
 * facts object per line (or reading), listed under every key it answers to,
 * so a reader that gathers several keys can recognise the same line twice and
 * count it once (`collectHits`). Line facts come from the transport log alone
 * (`transportLines`); readings from both logs. Packaging lines are skipped:
 * they are utensil/clamshell plan rows, never a served item, and their counts
 * would double an item's Ordered.
 */
export function indexDocumentFacts(doc: ProductionRecordDocument): Map<string, DocumentFacts[]> {
  const index = new Map<string, DocumentFacts[]>()
  const put = (key: string, facts: DocumentFacts): void => {
    index.set(key, [...(index.get(key) ?? []), facts])
  }

  for (const line of transportLines(doc)) {
    const facts: DocumentFacts = { ...EMPTY_FACTS, ordered: line.counts.ordered, prepared: line.counts.prepared }
    for (const key of identityKeys(line)) {
      put(key, facts)
    }
    put(`name:${normalizeLabel(line.name)}`, facts)
  }

  // Readings ride on BOTH logs' groups, keyed by the same identities. FACILITY
  // is the Kitchen °F fact and the stamp on its tip; DELIVERY is the Received
  // °F fact for an item with no truck on screen (and the stamp on the tip when
  // it has one).
  const readings = [
    ...doc.transport.groups.flatMap((group) => group.temps),
    ...doc.service.groups.flatMap((group) => group.temps),
  ]
  for (const reading of readings) {
    if (reading.checkpoint !== 'FACILITY' && reading.checkpoint !== 'DELIVERY') {
      continue
    }
    const value = readingValue(reading)
    const source = readingSource(reading)
    const facts: DocumentFacts =
      reading.checkpoint === 'FACILITY'
        ? { ...EMPTY_FACTS, kitchenTempF: value, kitchenLoggedAt: reading.loggedAt, kitchenSource: source }
        : { ...EMPTY_FACTS, deliveryTempF: value, deliveryLoggedAt: reading.loggedAt, deliverySource: source }
    const keys = identityKeys(reading)
    if (reading.lineName) {
      keys.push(`name:${normalizeLabel(reading.lineName)}`)
    }
    for (const key of keys) {
      put(key, facts)
    }
  }

  return index
}

/** One document transport line that names the band it covers. */
export interface DocumentBandFacts {
  /** `normalizeLabel(gradeSpan ?? gradeRangeName)`. */
  band: string
  prepared: number | null
  delivered: number | null
}

/**
 * The document lines that are SPLIT BY BAND, by identity, so a band row can
 * take its own line's Prepared / Received instead of a pro-rata share of the
 * item total (§4.3). The same rule `record-insights.ts` uses to attach a
 * transport line to a tier (`shippedBandOf`): a span the SHIPMENT or the ROSTER
 * put on the line names the band; a span the builder copied from the MENU
 * (`gradeSpanSource: 'MENU'`) is item-level legend text — the whole campus's
 * line, not one band's — so only the line's own `gradeRangeName` may still
 * name a band there. Without that gate a campus whose band is called what its
 * menu span reads (`K-12`, `K-8`, `PK-5`) hands that band the ENTIRE item's
 * Prepared / Received while its siblings keep their pro-rata share, and the
 * item prefills to more than was ever shipped. A line without the field is the
 * pre-PROD-1279 wire, where every span was a real band.
 */
export function indexDocumentBandLines(doc: ProductionRecordDocument): Map<string, DocumentBandFacts[]> {
  const index = new Map<string, DocumentBandFacts[]>()
  for (const line of transportLines(doc)) {
    const band = line.gradeSpanSource === 'MENU' ? line.gradeRangeName : (line.gradeSpan ?? line.gradeRangeName)
    if (!band) {
      continue
    }
    const facts: DocumentBandFacts = {
      band: normalizeLabel(band),
      prepared: line.counts.prepared,
      delivered: line.counts.delivered,
    }
    for (const key of [...identityKeys(line), `name:${normalizeLabel(line.name)}`]) {
      index.set(key, [...(index.get(key) ?? []), facts])
    }
  }
  return index
}

/**
 * Every index entry an item's identities reach, each source ONCE.
 *
 * An item can stand on SEVERAL document lines — a deliverable the fill merged
 * across two bundles (`lineTargets` `bd:a` and `bd:b`), a transport-only row
 * whose truck lines name two components — and each of those lines is a
 * different plan fact, so they ADD: a Beverage sold through two bundles that
 * prepared 60 and 40 prepared 100. What must never add is the same line seen
 * twice: a line is indexed under its bundle deliverable, its menu item and its
 * name, and a merged deliverable's per-tier targets repeat one line's ids, so
 * hits are gathered into a Set of the shared facts objects. Within ONE identity
 * group the keys are a ladder (bundle deliverable, then menu item) and the
 * first key that hits ends the group — a menu item's key can index every
 * deliverable of its bundle, and reading it beside the deliverable's own key
 * would blend the siblings in. The normalized name is the last resort, read
 * only when no identity hit at all: a name hit on top of an id hit is the same
 * line again, and a name hit beside a MISSED id is a namesake the id ruled out.
 */
function collectHits<T>(
  index: ReadonlyMap<string, readonly T[]>,
  identityGroups: readonly (readonly string[])[],
  label: string,
): T[] {
  const hits = new Set<T>()
  for (const group of identityGroups) {
    for (const key of group) {
      const found = index.get(key)
      if (found && found.length > 0) {
        for (const hit of found) {
          hits.add(hit)
        }
        break
      }
    }
  }
  if (hits.size === 0) {
    for (const hit of index.get(`name:${normalizeLabel(label)}`) ?? []) {
      hits.add(hit)
    }
  }
  return [...hits]
}

/** One identity group per line target: that target's keys, in match order. */
function deliverableIdentityGroups(deliverable: Pick<ProductionFillDeliverable, 'lineTargets'> | null): string[][] {
  return (deliverable?.lineTargets ?? []).map((target) => identityKeys(target)).filter((keys) => keys.length > 0)
}

export function documentFactsFor(
  index: ReadonlyMap<string, readonly DocumentFacts[]>,
  deliverable: Pick<ProductionFillDeliverable, 'label' | 'lineTargets'> | null,
  label: string,
  extraKeys: readonly string[] = [],
): DocumentFacts {
  const groups = [...deliverableIdentityGroups(deliverable), ...extraKeys.map((key) => [key])]
  return collectHits(index, groups, label).reduce(mergeFacts, EMPTY_FACTS)
}

export function documentBandFactsFor(
  index: ReadonlyMap<string, readonly DocumentBandFacts[]>,
  deliverable: Pick<ProductionFillDeliverable, 'label' | 'lineTargets'> | null,
  label: string,
): DocumentBandFacts[] {
  return collectHits(index, deliverableIdentityGroups(deliverable), label)
}

/**
 * One band's Prepared / Received over EVERY document line that names it, or
 * null when none does. A campus that splits deliveries (per grade, per
 * homeroom) has one transport line per shipment line, all carrying the same
 * band: a K-5 tier shipped as two grade shipments of 20 is two `K-5` lines,
 * and its band row prefills 40, not the first line's 20.
 */
export function bandLineTotals(
  lines: readonly DocumentBandFacts[],
  band: string,
): Pick<DocumentBandFacts, 'prepared' | 'delivered'> | null {
  const key = normalizeLabel(band)
  const matching = lines.filter((line) => line.band === key)
  if (matching.length === 0) {
    return null
  }
  return matching.reduce<Pick<DocumentBandFacts, 'prepared' | 'delivered'>>(
    (acc, line) => ({
      prepared: addNullable(acc.prepared, line.prepared),
      delivered: addNullable(acc.delivered, line.delivered),
    }),
    { prepared: null, delivered: null },
  )
}

/** Who signed for the kitchen on this record, and when. */
export function kitchenSignature(doc: ProductionRecordDocument): { name: string | null; signedAt: string | null } {
  for (const group of doc.transport.groups) {
    if (group.kitchenSignature.name) {
      return { name: group.kitchenSignature.name, signedAt: group.kitchenSignature.signedAt }
    }
  }
  return { name: null, signedAt: null }
}

/** Who signed for the kitchen on this record — the name on the chef-reading tip. */
export function kitchenSignatureName(doc: ProductionRecordDocument): string | null {
  return kitchenSignature(doc).name
}

/** The band a grade row stands for — the same ladder the Service records task shows. */
export function gradeLegend(row: ProductionFillGradeRow): string {
  return row.groupLabel ?? row.gradeGroupName ?? row.rosterName ?? 'All grades'
}

/** The legend line of a band row: `K-12 · 2 each`, or the band alone (D-B3.2). */
export function bandLegendText(band: string, portionLabel: string | null): string {
  return portionLabel ? `${band} · ${portionLabel}` : band
}

/**
 * `componentId` → the catalog identities its document line carries
 * (`bd:<bundleDeliverableId>`, `mi:<menuItemId>`).
 *
 * This is the bridge that makes the transport join EXACT. A delivery line names
 * the SKU the cafeteria counts ("Milk, Plain, 0% Fat Free Skim, Carton (8 oz)")
 * while a record deliverable names the package it belongs to ("0% Milk"), so no
 * amount of string work joins the two — but the line carries a `componentId`,
 * and the document's own line node for that component carries the deliverable
 * identity the fill wire's `lineTargets` also hold.
 */
export function indexDocumentComponents(doc: ProductionRecordDocument): Map<string, string[]> {
  const index = new Map<string, string[]>()
  for (const line of documentLines(doc)) {
    if (!line.componentId) {
      continue
    }
    const identities = identityKeys({ bundleDeliverableId: line.bundleDeliverableId, menuItemId: line.menuItemId })
    if (identities.length === 0) {
      continue
    }
    const existing = index.get(line.componentId)
    if (existing) {
      for (const identity of identities) {
        if (!existing.includes(identity)) {
          existing.push(identity)
        }
      }
    } else {
      index.set(line.componentId, identities)
    }
  }
  return index
}

/**
 * The recipes inside each sealed kit, per kit identity (`bd:` / `mi:` of the
 * kit node), from the document's kit nodes and their count-less sub-rows
 * (PROD-1441). The shipped composition is the truth for a record: the live
 * catalog reuses a positional deliverable id with different contents on
 * re-author. A kit shipped per band is one kit node per band with the same
 * recipes; the first kit seen names them.
 */
export function indexKitComponents(doc: ProductionRecordDocument): Map<string, KitComponent[]> {
  const out = new Map<string, KitComponent[]>()
  for (const line of documentLines(doc)) {
    if (line.isKit !== true || line.children.length === 0) {
      continue
    }
    const components: KitComponent[] = []
    for (const child of line.children) {
      if (!child.componentId || components.some((component) => component.componentId === child.componentId)) {
        continue
      }
      components.push({
        componentId: child.componentId,
        name: child.name,
        servingSize: child.recipeServingSize ?? child.servingLabel,
        recipeCode: child.recipeCode ?? null,
        kindLabel: child.kindLabel,
        componentKind: child.componentKind ?? null,
      })
    }
    // Keyed by the kit's DELIVERABLE id only. Its menu item is the whole
    // meal's and would file these recipes under every package of the bundle.
    const key = line.bundleDeliverableId ? `bd:${line.bundleDeliverableId}` : null
    if (key !== null && !out.has(key)) {
      out.set(key, components)
    }
  }
  return out
}

/** The first TOP-LEVEL document line per identity (`bd:` / `mi:` / `co:`), for a deliverable's kind and section. */
export function indexLineIdentities(doc: ProductionRecordDocument): Map<string, DocumentLineNode> {
  const out = new Map<string, DocumentLineNode>()
  for (const line of [
    ...doc.transport.groups.flatMap((group) => group.lines),
    ...doc.service.groups.flatMap((group) => group.lines),
  ]) {
    if (line.isPackaging) {
      continue
    }
    for (const key of identityKeys(line)) {
      if (!out.has(key)) {
        out.set(key, line)
      }
    }
  }
  return out
}

/** The lookups a mealtime's deliverables offer a truck row, in match order. */
interface DeliverableLookup {
  /** `bd:…` / `mi:…` → deliverable index. EXACT. */
  byIdentity: Map<string, number>
  /** The component a package IS → deliverable index. EXACT; joins a weekly milk SKU to the milk it was ordered as. */
  byComponent: Map<string, number>
  /** `${normalized label}|${class}` → deliverable index. Distinguishes a hot and a chilled namesake. */
  byNameClass: Map<string, number>
  /** `${normalized label}` → deliverable index. The last resort. */
  byName: Map<string, number>
}

function nameClassKey(label: string, temperatureClass: TemperatureClass | null): string {
  return `${normalizeLabel(label)}|${temperatureClass ?? ''}`
}

export function buildDeliverableLookup(deliverables: readonly ProductionFillDeliverable[]): DeliverableLookup {
  const byIdentity = new Map<string, number>()
  const byComponent = new Map<string, number>()
  const byNameClass = new Map<string, number>()
  const byName = new Map<string, number>()
  deliverables.forEach((deliverable, i) => {
    if (deliverable.componentId && !byComponent.has(deliverable.componentId)) {
      byComponent.set(deliverable.componentId, i)
    }
    for (const target of deliverable.lineTargets) {
      for (const key of identityKeys(target)) {
        if (!byIdentity.has(key)) {
          byIdentity.set(key, i)
        }
      }
    }
    const classKey = nameClassKey(deliverable.label, deliverable.temperatureClass)
    if (!byNameClass.has(classKey)) {
      byNameClass.set(classKey, i)
    }
    const key = normalizeLabel(deliverable.label)
    if (!byName.has(key)) {
      byName.set(key, i)
    }
  })
  return { byIdentity, byComponent, byNameClass, byName }
}

/**
 * Which record deliverable a truck row belongs to, or null.
 *
 * THREE steps, in descending confidence:
 *   1. the ID CHAIN — the line's `componentId`, through the document's line node
 *      for that component, to the deliverable identity the fill wire carries;
 *      else straight to the deliverable that IS that component (the fill's
 *      `componentId`). Exact; it is what joins a milk SKU to its package. The
 *      weekly milk drop's lines carry no order identity, so the document's
 *      milk line has none to offer and only the direct step can join it;
 *   2. the line's own `deliverableName` — the package the vendor named, matched
 *      against the deliverable label (class-qualified first);
 *   3. the row LABEL, class-qualified then bare. The only step available when
 *      the document has not loaded, and the only one that can be wrong.
 */
export function matchDeliverableIndex(
  row: AggregatedDeliveryRow,
  lookup: DeliverableLookup,
  componentIdentities: ReadonlyMap<string, string[]>,
): number | null {
  for (const part of row.parts) {
    if (!part.componentId) {
      continue
    }
    for (const identity of componentIdentities.get(part.componentId) ?? []) {
      const hit = lookup.byIdentity.get(identity)
      if (hit !== undefined) {
        return hit
      }
    }
    const direct = lookup.byComponent.get(part.componentId)
    if (direct !== undefined) {
      return direct
    }
  }
  for (const part of row.parts) {
    if (!part.deliverableName) {
      continue
    }
    const hit =
      lookup.byNameClass.get(nameClassKey(part.deliverableName, row.temperatureClass)) ??
      lookup.byName.get(normalizeLabel(part.deliverableName))
    if (hit !== undefined) {
      return hit
    }
  }
  return (
    lookup.byNameClass.get(nameClassKey(row.label, row.temperatureClass)) ??
    lookup.byName.get(normalizeLabel(row.label)) ??
    null
  )
}

/**
 * The fallback date on a `Confirmed on …` tip, for a shipment whose `receivedAt`
 * the wire did not carry.
 */
export function confirmedDateLabel(deliveryDate: string): string {
  return formatServeHeaderDateLabel(deliveryDate)
}

/** Vendor + age ranges under an item — the legend the Transport logs task renders. */
export function transportRowLegend(row: AggregatedDeliveryRow): string | null {
  const detail = [row.vendorName, ...row.ageRangeNames].filter(Boolean).join(', ')
  return detail === '' ? null : detail
}

/**
 * Which of a day's trucks are the WEEKLY MILK DROP (every line a milk-stamped
 * line) and which are meal trucks. `splitMilkDeliveries` strips the milk lines
 * off EVERY shipment, which suits the shipped Transport logs task (one screen
 * per delivery date) but not a screen split by mealtime: a lunch truck that
 * also carries a milk line would show its meal lines on the Lunch step and
 * its milk line on the first step, and a receipt is posted per SHIPMENT with
 * every line — from either step the other step's lines arrive without a
 * temperature and the server refuses the whole receipt
 * (`missing_delivery_temperature`), with no cell on screen to explain it. So
 * only a milk-ONLY shipment is the weekly drop here; a mixed shipment stays
 * whole on its own mealtime's step, its milk line an ordinary chilled row.
 */
export function partitionMilkOnlyDeliveries(deliveries: readonly CafeteriaDeliveryWire[]): {
  milkOnly: CafeteriaDeliveryWire[]
  meal: CafeteriaDeliveryWire[]
} {
  const milkOnly: CafeteriaDeliveryWire[] = []
  const meal: CafeteriaDeliveryWire[] = []
  for (const delivery of deliveries) {
    const isMilkOnly = delivery.lines.length > 0 && delivery.lines.every((line) => isMilkDeliveryLine(delivery, line))
    if (isMilkOnly) {
      milkOnly.push(delivery)
    } else {
      meal.push(delivery)
    }
  }
  return { milkOnly, meal }
}

// ---------------------------------------------------------------------------
// Prefill ladders
// ---------------------------------------------------------------------------

/**
 * Share an item total across its bands pro-rata by planned servings — the
 * SAME rule `distributeReceivedTotal` applies to shipment lines (whole units,
 * remainder to the earliest bands, a zero plan splits evenly). Pure.
 */
export function apportionByPlanned(total: number, planned: readonly number[]): number[] {
  if (planned.length === 0) {
    return []
  }
  return distributeReceivedTotal(
    total,
    planned.map((plannedQuantity, i) => ({
      shipmentId: String(i),
      shipmentLineId: String(i),
      plannedQuantity,
      receivedQuantity: null,
      tempDefault: null,
      cafeteriaCountedQuantity: null,
      cafeteriaTemperature: null,
    })),
  ).map((share) => share.receivedQuantity)
}

/**
 * WHOSE reading pre-fills a Received °F box, mirroring `lineTempDefault`'s own
 * ladder: the cafeteria's receipt reading, then the chef's (delivery, then
 * facility), then a bare class preset nobody measured.
 */
export function transportTempSource(
  row: Pick<AggregatedDeliveryRow, 'tempDefault' | 'cafeteriaTemperature' | 'parts'>,
  pending: boolean,
): 'cafeteria' | 'confirmed' | 'kitchen' | 'preset' | null {
  if (row.tempDefault === null) {
    return null
  }
  if (row.cafeteriaTemperature != null) {
    return 'cafeteria'
  }
  // The prefill has to BE somebody's reading, not merely sit on a row that has
  // one. `tempDefault` is the FIRST part's ladder value, so on a group-split
  // campus a homeroom with no chef reading contributes the bare class preset
  // while a sibling homeroom carries 171: attributing that 165 to the kitchen
  // would put a provenance claim on a number nobody measured.
  const isChefReading = row.parts.some(
    (part) => part.deliveryTemperatureF === row.tempDefault || part.facilityTemperatureF === row.tempDefault,
  )
  if (!isChefReading) {
    return 'preset'
  }
  // The wire has ONE `deliveryTemperatureF` per line, whoever wrote it. On a
  // truck still awaiting receipt nobody but the kitchen can have; on a received
  // one it is that receipt's, which is what the tip should say.
  return pending ? 'kitchen' : 'confirmed'
}

/** The chef's FACILITY reading among these lines, or null. NO class preset: a
 * kitchen temperature nobody took is not a number to suggest. */
export function kitchenTempDefaultFor(row: Pick<AggregatedDeliveryRow, 'parts'>): number | null {
  for (const part of row.parts) {
    if (part.facilityTemperatureF != null) {
      return part.facilityTemperatureF
    }
  }
  return null
}

/** Where an item's Received count prefills from, over every truck line it stands on. */
export interface TruckReceivedFacts {
  value: number | null
  /** `confirmed` = every line received; `logged` = the cafeteria counted some; `plan` = the truck's plan. */
  kind: 'confirmed' | 'logged' | 'plan' | null
  /** Σ of the cafeteria's counts, for the `counted N` tip. */
  counted: number | null
  confirmedAt: string | null
  confirmedBy: string | null
  dateLabel: string
}

/**
 * Servings per truck package for a row (Amana, 2026-11-03): one orange-wedge
 * package line of 100 carries two wedge cups each, 200 servings, so the row
 * that orders, prepares and serves 200 must read the truck's 100 as 200, never
 * flag "Served above Received". The ratio of every line's contents to its
 * packages, when they all agree on a whole number above 1; else 1. Weekly milk
 * lines are cartons, always 1.
 */
export function truckUnitScale(trucks: readonly TruckLineRef[]): number {
  let scale: number | null = null
  for (const truck of trucks) {
    const contents = truck.contentsQuantity ?? null
    if (truck.weeklyMilk || contents === null || truck.plannedQuantity <= 0) {
      return 1
    }
    const ratio = contents / truck.plannedQuantity
    if (!Number.isInteger(ratio) || (scale !== null && ratio !== scale)) {
      return 1
    }
    scale = ratio
  }
  return scale !== null && scale > 1 ? scale : 1
}

/** A row's truck facts in servings: every count times `truckUnitScale`. */
export function scaleReceivedFacts(facts: TruckReceivedFacts, scale: number): TruckReceivedFacts {
  if (scale === 1) {
    return facts
  }
  return {
    ...facts,
    value: facts.value === null ? null : facts.value * scale,
    counted: facts.counted === null ? null : facts.counted * scale,
  }
}

export function truckReceivedFacts(trucks: readonly TruckLineRef[]): TruckReceivedFacts {
  if (trucks.length === 0) {
    return { value: null, kind: null, counted: null, confirmedAt: null, confirmedBy: null, dateLabel: '' }
  }
  const first = trucks[0]!
  const base = {
    confirmedAt: trucks.find((t) => t.receivedAt)?.receivedAt ?? null,
    confirmedBy: trucks.find((t) => t.receiverName)?.receiverName ?? null,
    dateLabel: confirmedDateLabel(first.deliveryDate),
  }
  if (trucks.every((t) => t.receivedQuantity != null)) {
    return {
      ...base,
      value: trucks.reduce((sum, t) => sum + (t.receivedQuantity ?? 0), 0),
      kind: 'confirmed',
      counted: null,
    }
  }
  const counted = trucks.reduce<number | null>(
    (sum, t) => (t.cafeteriaCountedQuantity === null ? sum : (sum ?? 0) + t.cafeteriaCountedQuantity),
    null,
  )
  const value = trucks.reduce(
    (sum, t) => sum + (t.receivedQuantity ?? t.cafeteriaCountedQuantity ?? t.plannedQuantity),
    0,
  )
  return { ...base, value, kind: counted === null ? 'plan' : 'logged', counted }
}

function sourceTip(label: string): PrefillTip {
  return { kind: 'source', label }
}

function savedTip(value: number): PrefillTip {
  return { kind: 'source', label: `Saved on this record: ${value}`, saved: true }
}

function savedTempTip(value: number): PrefillTip {
  return { kind: 'source', label: `Saved on this record: ${formatTemperatureF(value)}`, saved: true }
}

const EMPTY_VALUE: PrefillValue = { value: null, tip: null }

/** The legend token and the tip stem of every weekly milk row. */
export const WEEKLY_MILK_LEGEND = 'Weekly milk delivery'
export const NOT_ON_RECORD_LEGEND = 'not on this record'
const WEEKLY_MILK_TIP = 'From the weekly milk delivery'

function receivedPrefill(
  facts: TruckReceivedFacts,
  value: number | null,
  counted: number | null,
  planLabel = 'From the delivery plan',
): PrefillValue {
  if (value === null || facts.kind === null) {
    return EMPTY_VALUE
  }
  switch (facts.kind) {
    case 'confirmed':
      return {
        value,
        tip: { kind: 'confirmed', at: facts.confirmedAt, byName: facts.confirmedBy, dateLabel: facts.dateLabel },
      }
    case 'logged':
      return {
        value,
        tip: {
          kind: 'logged',
          label: `counted ${counted ?? facts.counted ?? value}`,
          loggedAt: null,
          loggedByName: null,
        },
      }
    case 'plan':
      return { value, tip: sourceTip(`${planLabel}: ${value}`) }
  }
}

/** One truck line, with the group that carried it. */
interface TransportEntry {
  group: SpreadsheetTransportGroup | SpreadsheetMilkGroup
  row: AggregatedDeliveryRow
}

type MilkEntry = TransportEntry & { group: SpreadsheetMilkGroup }

/** A weekly milk SKU line, as opposed to a meal truck's. */
function isMilkEntry(entry: TransportEntry): entry is MilkEntry {
  return 'group' in entry.group
}

/**
 * The shared scope of the weekly milk SKU(s) feeding one row. One SKU per row
 * is the rule; a deliverable two SKUs both resolve to (or one SKU on two drop
 * dates) gets a composite key, so identical sets still share.
 */
function milkScopeFor(entries: readonly MilkEntry[]): MilkScope {
  const sorted = [...entries].sort((a, b) => a.group.deliveryDate.localeCompare(b.group.deliveryDate))
  const lead = sorted[0]!
  const dropKey = milkDropKey(lead)
  const skuKeys = [...new Set(sorted.map((entry) => `${entry.group.deliveryDate}::${entry.row.key}`))].sort()
  const scopeKey = `${dropKey}::${skuKeys.join('+')}`
  return { dropKey, scopeKey, storeKey: `${TRANSPORT_ROW_PREFIX}${scopeKey}` }
}

/** `milk::${deliveryDate}::${vendorLocationId}` — the drop. */
function milkDropKey(entry: MilkEntry): string {
  return `milk::${entry.group.deliveryDate}::${entry.group.group.vendorLocationId}`
}

/** `Weekly milk delivery[ · vendor][ · not on this record]`. */
function milkLegend(entry: MilkEntry, notOnRecord: boolean): string {
  return [WEEKLY_MILK_LEGEND, entry.group.group.vendorName, notOnRecord ? NOT_ON_RECORD_LEGEND : null]
    .filter(Boolean)
    .join(' · ')
}

/**
 * The weekly drop's SKU as a row's three transport prefills: the TRUCK's
 * numbers on every row the SKU feeds, never a band's share and never a stored
 * per-band figure (the scope is the truck's, and a band figure this screen
 * stamped before the join would read `Saved on this record: 10` against a
 * 46-carton drop). `todaysOrder` is the band's own order, kept visible in the
 * Ordered tip beside the truck's count.
 */
function weeklyMilkPrefill(
  entries: readonly MilkEntry[],
  received: TruckReceivedFacts,
  band: Pick<ProductionFillGradeRow, 'planned' | 'ordered'> | null,
): Pick<RecordRowPrefill, 'ordered' | 'prepared' | 'received'> {
  const planned = plannedTotalOf(entries) ?? 0
  return {
    ordered: weeklyMilkOrdered(planned, band),
    prepared: { value: planned, tip: sourceTip(`${WEEKLY_MILK_TIP}: ${planned}`) },
    received: receivedPrefill(received, received.value, received.counted, WEEKLY_MILK_TIP),
  }
}

/**
 * A weekly milk row's Ordered. On a record band it is the MEAL's own figure,
 * saved on the band like Started with (Amana, 2026-11-03: the school corrects
 * it, and the truck's weekly count stamped on every meal's record would print
 * the drop once per meal): the stored number, else today's order for the
 * band. A row on no band keeps the truck's count.
 */
function weeklyMilkOrdered(
  truckPlanned: number,
  band: Pick<ProductionFillGradeRow, 'planned' | 'ordered'> | null,
): PrefillValue {
  if (band === null) {
    return { value: truckPlanned, tip: sourceTip(`${WEEKLY_MILK_TIP}: ${truckPlanned}`) }
  }
  const stored = band.ordered ?? null
  if (stored !== null) {
    return { value: stored, tip: savedTip(stored) }
  }
  return {
    value: band.planned,
    tip: sourceTip(`From the order: ${band.planned} · ${WEEKLY_MILK_TIP.toLowerCase()}: ${truckPlanned}`),
  }
}

function truckRefs(entries: readonly TransportEntry[]): TruckLineRef[] {
  const refs: TruckLineRef[] = []
  for (const entry of [...entries].sort((a, b) => a.group.deliveryDate.localeCompare(b.group.deliveryDate))) {
    for (const part of entry.row.parts) {
      refs.push({
        ...part,
        deliveryDate: entry.group.deliveryDate,
        rowKey: entry.row.key,
        pending: entry.group.pending,
        receivedAt: entry.group.receivedAt,
        receiverName: entry.group.receiverName,
        weeklyMilk: isMilkEntry(entry),
      })
    }
  }
  return refs
}

/** Σ of the truck lines' planned quantities, or null when no truck carries the item. */
function plannedTotalOf(entries: readonly TransportEntry[]): number | null {
  if (entries.length === 0) {
    return null
  }
  return entries.flatMap((entry) => entry.row.parts).reduce((sum, part) => sum + part.plannedQuantity, 0)
}

/** The item-level Kitchen °F and Received °F prefills, shared by every row of the item. */
function itemTempPrefills(
  entries: readonly TransportEntry[],
  facts: DocumentFacts,
  kitchenName: string | null,
  temperatureClass: TemperatureClass | null,
  milkReading: Pick<MilkReceiptGroup, 'tempDefault' | 'cafeteriaTemperature'> | null,
): { kitchenTemp: PrefillTemp; receivedTemp: PrefillTemp } {
  const offered = tempCheckRequired(temperatureClass)
  const parts = entries.flatMap((entry) => entry.row.parts)
  const kitchenTip = (value: number, loggedAt: string | null): PrefillTip => ({
    kind: 'kitchen',
    label: formatTemperatureF(value),
    loggedAt,
    loggedByName: kitchenName,
  })

  // Kitchen °F: the chef's own reading on the truck, else the document's
  // FACILITY reading, else nothing. Never a class preset.
  let kitchenTemp: PrefillTemp = { value: null, tip: null, offered, fromShipmentEvent: false }
  const chefKitchen = kitchenTempDefaultFor({ parts })
  if (chefKitchen !== null) {
    kitchenTemp = {
      value: chefKitchen,
      tip: kitchenTip(chefKitchen, facts.kitchenLoggedAt),
      offered,
      fromShipmentEvent: true,
    }
  } else if (facts.kitchenTempF !== null) {
    kitchenTemp =
      facts.kitchenSource === 'PRODUCTION_RECORD'
        ? { value: facts.kitchenTempF, tip: savedTempTip(facts.kitchenTempF), offered, fromShipmentEvent: false }
        : {
            value: facts.kitchenTempF,
            tip: kitchenTip(facts.kitchenTempF, facts.kitchenLoggedAt),
            offered,
            fromShipmentEvent: true,
          }
  }

  // Received °F: the truck's own ladder (cafeteria → receipt → chef → preset),
  // else the document's DELIVERY reading, else the class preset, else nothing.
  let receivedTemp: PrefillTemp = { value: null, tip: null, offered, fromShipmentEvent: false }
  const sorted = [...entries].sort((a, b) => a.group.deliveryDate.localeCompare(b.group.deliveryDate))
  const lead = sorted.find((entry) => (milkReading ?? entry.row).tempDefault !== null)
  if (lead) {
    const reading = milkReading ?? lead.row
    const value = reading.tempDefault!
    const source = transportTempSource({ ...reading, parts }, lead.group.pending)
    const tip: PrefillTip | null =
      source === 'cafeteria' && reading.cafeteriaTemperature
        ? {
            kind: 'logged',
            label: formatTemperatureF(reading.cafeteriaTemperature.value),
            loggedAt: reading.cafeteriaTemperature.loggedAt,
            loggedByName: reading.cafeteriaTemperature.loggedByName,
          }
        : source === 'kitchen'
          ? kitchenTip(value, facts.deliveryLoggedAt ?? facts.kitchenLoggedAt)
          : source === 'confirmed'
            ? {
                kind: 'confirmed',
                at: lead.group.receivedAt,
                byName: lead.group.receiverName,
                dateLabel: confirmedDateLabel(lead.group.deliveryDate),
              }
            : null
    receivedTemp = { value, tip, offered, fromShipmentEvent: true }
  } else if (facts.deliveryTempF !== null) {
    receivedTemp =
      facts.deliverySource === 'PRODUCTION_RECORD'
        ? { value: facts.deliveryTempF, tip: savedTempTip(facts.deliveryTempF), offered, fromShipmentEvent: false }
        : {
            value: facts.deliveryTempF,
            tip: sourceTip(`Recorded at delivery: ${formatTemperatureF(facts.deliveryTempF)}`),
            offered,
            fromShipmentEvent: true,
          }
  } else if (offered && temperatureClass) {
    const preset = TEMP_CLASS_PREFILL_F[temperatureClass] ?? null
    receivedTemp = { value: preset, tip: null, offered, fromShipmentEvent: false }
  }

  return { kitchenTemp, receivedTemp }
}

/**
 * WHOSE reading the fill's `serviceTemperature.prefill` is. The server folds
 * the item's latest FACILITY event, else its latest DELIVERY event, into one
 * number and the wire carries no source with it; a DELIVERY event on a
 * RECEIVED truck is the school's own receipt reading, so calling every prefill
 * the kitchen's would put a provenance claim — with the chef's name on it — on
 * a compliance screen about a number the school entered. The truck lines and
 * the document carry those same events by value, so the value is matched back:
 *   - a FACILITY match is the kitchen's reading;
 *   - a DELIVERY match is the kitchen's hand-off reading while every truck of
 *     the item is still out, else the receipt it came in on (`Confirmed on …`),
 *     else — no truck on screen to say whose — `Recorded at delivery`;
 *   - a value matching nothing gets NO attribution rather than a wrong one.
 * OPEN (server): a source beside `prefill` on the fill wire would settle this
 * without the value match.
 */
function servingPrefillTip(
  value: number,
  facts: DocumentFacts,
  kitchenName: string | null,
  trucks: readonly TruckLineRef[],
): PrefillTip | null {
  const kitchenTip = (loggedAt: string | null): PrefillTip => ({
    kind: 'kitchen',
    label: formatTemperatureF(value),
    loggedAt,
    loggedByName: kitchenName,
  })
  const facilityMatch =
    trucks.some((truck) => truck.facilityTemperatureF === value) ||
    (facts.kitchenSource === 'SHIPMENT_EVENT' && facts.kitchenTempF === value)
  if (facilityMatch) {
    return kitchenTip(facts.kitchenLoggedAt)
  }
  const deliveryMatch =
    trucks.some((truck) => truck.deliveryTemperatureF === value) ||
    (facts.deliverySource === 'SHIPMENT_EVENT' && facts.deliveryTempF === value)
  if (!deliveryMatch) {
    return null
  }
  if (trucks.length > 0 && trucks.every((truck) => truck.pending)) {
    return kitchenTip(facts.deliveryLoggedAt)
  }
  const receipt = trucks.find((truck) => !truck.pending)
  if (receipt) {
    return {
      kind: 'confirmed',
      at: receipt.receivedAt,
      byName: receipt.receiverName,
      dateLabel: confirmedDateLabel(receipt.deliveryDate),
    }
  }
  return sourceTip(`Recorded at delivery: ${formatTemperatureF(value)}`)
}

/** The serving reading's ladder: stored → cafeteria → chef → class preset (`seedProductionEdits`). */
function servingPrefill(
  deliverable: ProductionFillDeliverable,
  facts: DocumentFacts,
  kitchenName: string | null,
  trucks: readonly TruckLineRef[],
): PrefillTemp {
  const offered = tempCheckRequired(deliverable.temperatureClass)
  const st = deliverable.serviceTemperature
  if (st.value != null) {
    return { value: st.value, tip: savedTempTip(st.value), offered, fromShipmentEvent: false }
  }
  if (st.cafeteria) {
    return {
      value: st.cafeteria.value,
      tip: {
        kind: 'logged',
        label: formatTemperatureF(st.cafeteria.value),
        loggedAt: st.cafeteria.loggedAt,
        loggedByName: st.cafeteria.loggedByName,
      },
      offered,
      fromShipmentEvent: false,
    }
  }
  if (st.prefill != null) {
    return {
      value: st.prefill,
      tip: servingPrefillTip(st.prefill, facts, kitchenName, trucks),
      offered,
      fromShipmentEvent: true,
    }
  }
  const preset = deliverable.temperatureClass ? (TEMP_CLASS_PREFILL_F[deliverable.temperatureClass] ?? null) : null
  return { value: offered ? preset : null, tip: null, offered, fromShipmentEvent: false }
}

const NO_SERVING: (temperatureClass: TemperatureClass | null) => PrefillTemp = (temperatureClass) => ({
  value: null,
  tip: null,
  offered: tempCheckRequired(temperatureClass),
  fromShipmentEvent: false,
})

// ---------------------------------------------------------------------------
// The build
// ---------------------------------------------------------------------------

/** Everything one record contributes to the join, computed once per build. */
interface RecordContext {
  doc: ProductionRecordDocument | null
  index: Map<string, DocumentFacts[]>
  bandLines: Map<string, DocumentBandFacts[]>
  componentIdentities: Map<string, string[]>
  /** `componentId` → the document line node, for a transport-only row's save identity. */
  componentNodes: Map<string, DocumentLineNode>
  /** Kit identity → the recipes inside, for the kit rows' sub-rows (PROD-1441). */
  kits: Map<string, KitComponent[]>
  /** Identity → the first top-level document line, for a deliverable's section and kind order (PROD-1441). */
  lineByIdentity: Map<string, DocumentLineNode>
  kitchenName: string | null
  lookup: DeliverableLookup
  /**
   * The document's KITCHEN, normalized — the only vendor identity a production
   * record carries (the fill wire has none). Null until the document lands,
   * which is why it can only ever NARROW a match, never force one.
   */
  vendorKey: string | null
}

function emptyContext(): RecordContext {
  return {
    doc: null,
    index: new Map(),
    bandLines: new Map(),
    componentIdentities: new Map(),
    componentNodes: new Map(),
    kits: new Map(),
    lineByIdentity: new Map(),
    kitchenName: null,
    lookup: buildDeliverableLookup([]),
    vendorKey: null,
  }
}

function recordContext(mealtime: ProductionFillMealtime, doc: ProductionRecordDocument | null): RecordContext {
  const componentNodes = new Map<string, DocumentLineNode>()
  if (doc) {
    for (const line of documentLines(doc)) {
      if (line.componentId && !componentNodes.has(line.componentId)) {
        componentNodes.set(line.componentId, line)
      }
    }
  }
  return {
    doc,
    index: doc ? indexDocumentFacts(doc) : new Map(),
    bandLines: doc ? indexDocumentBandLines(doc) : new Map(),
    componentIdentities: doc ? indexDocumentComponents(doc) : new Map(),
    componentNodes,
    kits: doc ? indexKitComponents(doc) : new Map(),
    lineByIdentity: doc ? indexLineIdentities(doc) : new Map(),
    kitchenName: doc ? kitchenSignatureName(doc) : null,
    lookup: buildDeliverableLookup(mealtime.deliverables),
    vendorKey: doc ? normalizeLabel(doc.kitchen.name) : null,
  }
}

/** True when this record may claim this truck line. Unknown on either side = yes. */
function vendorAllows(context: RecordContext, row: AggregatedDeliveryRow): boolean {
  if (context.vendorKey === null || row.vendorName === null) {
    return true
  }
  return normalizeLabel(row.vendorName) === context.vendorKey
}

/** The legend of a kit row on the input: how many recipes ride inside, counts per kit (PROD-1441). */
export function kitRowLegend(componentCount: number): string {
  return `${componentCount} recipes packed together · counts are per kit`
}

/** The tag beside a kit's title on the input table: `Sealed kit · 2 recipes`. */
export function kitBadge(componentCount: number): string {
  return `Sealed kit · ${componentCount} recipes`
}

/** The line under a kit's title on the input table: the counts belong to the kit, not to a recipe. */
export const KIT_COUNTS_NOTE = 'Counts are per kit'

/**
 * The meal pattern a record's menu was planned for (`PK-8`), when the
 * document's lines name exactly one `menuGradeSpan`, else null. The input's
 * grade-group heading reads it the way the paper's `gradeGroupHeading` does.
 */
export function documentMealPattern(doc: ProductionRecordDocument | null | undefined): string | null {
  if (!doc) {
    return null
  }
  const spans = new Set<string>()
  for (const line of documentLines(doc)) {
    if (line.menuGradeSpan) {
      spans.add(line.menuGradeSpan)
    }
  }
  return spans.size === 1 ? [...spans][0]! : null
}

/**
 * The one line the input table shows above its rows when Served is above
 * Received anywhere on the step, in place of a note under every such row
 * (PROD-1441, the kit outline): `Served is above Received on 10 rows, 8 in K-8
 * and 2 in 9-12.` The band split appears only when two or more bands carry
 * one. Null when no row does.
 */
export function servedOverReceivedHeadline(byBand: ReadonlyArray<{ band: string; count: number }>): string | null {
  const flagged = byBand.filter((entry) => entry.count > 0)
  const total = flagged.reduce((sum, entry) => sum + entry.count, 0)
  if (total === 0) {
    return null
  }
  const head = `Served is above Prepared on ${total} ${total === 1 ? 'row' : 'rows'}`
  if (flagged.length < 2) {
    return `${head}.`
  }
  const parts = flagged.map((entry) => `${entry.count} in ${entry.band}`)
  return `${head}, ${parts.slice(0, -1).join(', ')} and ${parts.at(-1)!}.`
}

/** After the headline: backstock explains it, so it never blocks signing. */
export const SERVED_OVER_RECEIVED_NOTE =
  'Prepared counts everything on hand, backstock included: correct Served or Prepared.'

/** The sections a step's rows are grouped under, in print order (PROD-1441). */
export const RECORD_ROW_SECTIONS: readonly RecordRowSection[] = ['MAIN', 'SIDE', 'CONDIMENT']

export const RECORD_ROW_SECTION_TITLE: Record<RecordRowSection, string> = {
  MAIN: 'Main choices',
  SIDE: 'Shared sides & milk',
  CONDIMENT: 'Condiments',
}

/** The group label of a row the record never banded (a truck line, a band-less deliverable). */
export const ALL_GRADES_GROUP = 'All grades'

/** The words before the band on a grade group's title. */
export const GRADE_GROUP_LABEL = 'Grade group'

/**
 * A grade group's section title, set directly above its own table (PROD-1441,
 * Abner 2026-10-08): `Grade group K-8`. Rows that carry no band are titled
 * `All grades` on its own. Every group is titled this way, one band or several,
 * on the desktop table and in the phone review alike.
 */
export function gradeGroupTitle(band: string): string {
  return band === ALL_GRADES_GROUP ? ALL_GRADES_GROUP : `${GRADE_GROUP_LABEL} ${band}`
}

/**
 * The section a deliverable's rows print in, from the document: a kit holding
 * a main entrée or a standalone main is a MAIN CHOICE; a condiment (or a kit
 * of nothing but condiments) is a CONDIMENT; everything else, milk included,
 * is a SHARED SIDE. With no document everything is a side.
 */
function sectionOfKinds(kinds: readonly (string | null)[]): RecordRowSection {
  const known = kinds.filter((kind): kind is string => kind !== null)
  if (known.includes('MAIN_ENTREE')) {
    return 'MAIN'
  }
  if (known.length > 0 && known.every((kind) => kind === 'CONDIMENT')) {
    return 'CONDIMENT'
  }
  return 'SIDE'
}

/** What the document says about one deliverable's place on the paper: its kit, its section, its kind rank. */
interface DeliverablePlacement {
  kit: RecordRowKit | null
  section: RecordRowSection
  sortRank: number
}

/**
 * The identities a line target may be looked up by. A PACKAGE is its
 * deliverable id alone: the bundle's menu item is shared by every package of
 * the meal, so reading it beside the id hands a package a sibling's recipes
 * and kind (an Orange Wedges cup took the mac kit's "in kit" sub-rows and its
 * Main choices slot, Larkspur 2026-10-08). An à-la-carte target has no
 * deliverable id and keeps its menu item / component keys.
 */
function placementKeys(target: ProductionFillLineTarget): string[] {
  return target.bundleDeliverableId ? [`bd:${target.bundleDeliverableId}`] : identityKeys(target)
}

/**
 * The row's title on the input: the record's own catalog name for the item
 * (`Milk, Chocolate, 1% Lowfat, Carton (8 oz)`), so the screen Marsha types
 * into names every row the way the saved record and the paper do (Amana,
 * 2026-11-04: `1% Choco Milk` on the input, the catalog name on the record).
 * A document name that is the fill's label plus a bundle disambiguation tail
 * (`Orange Wedges (BBQ Pulled Chicken Sando)`) reads as the bare label: the
 * input's row already stands for every pack of the item. Falls back to the
 * fill's label when the document has no line for the row, or names it two
 * different ways. Pure.
 */
function catalogLabelFor(deliverable: ProductionFillDeliverable, context: RecordContext): string {
  const names = new Set<string>()
  for (const target of deliverable.lineTargets) {
    for (const key of placementKeys(target)) {
      const line = context.lineByIdentity.get(key)
      if (line) {
        names.add(line.name.startsWith(`${deliverable.label} (`) ? deliverable.label : line.name)
        break
      }
    }
  }
  return names.size === 1 ? [...names][0]! : deliverable.label
}

function placementFor(deliverable: ProductionFillDeliverable, context: RecordContext): DeliverablePlacement {
  const kitComponents: KitComponent[] = []
  const kinds: (string | null)[] = []
  for (const target of deliverable.lineTargets) {
    for (const key of placementKeys(target)) {
      const components = context.kits.get(key)
      if (components) {
        for (const component of components) {
          if (!kitComponents.some((existing) => existing.componentId === component.componentId)) {
            kitComponents.push(component)
          }
        }
      }
      const line = context.lineByIdentity.get(key)
      if (line) {
        kinds.push(line.componentKind ?? null)
      }
    }
  }
  if (kitComponents.length >= 2) {
    const componentKinds = kitComponents.map((component) => component.componentKind)
    return {
      kit: { components: kitComponents },
      section: sectionOfKinds(componentKinds),
      sortRank: Math.min(...componentKinds.map(kindRank)),
    }
  }
  return { kit: null, section: sectionOfKinds(kinds), sortRank: rowKindRank(kinds) }
}

/** One flat row per band of a record deliverable (§4.2); a sealed kit is ONE row with its recipes as `kit.components` (PROD-1441). */
function deliverableRows(
  stepKey: string,
  mealtime: ProductionFillMealtime,
  deliverable: ProductionFillDeliverable,
  entries: readonly TransportEntry[],
  context: RecordContext,
): RecordRow[] {
  const facts = documentFactsFor(context.index, deliverable, deliverable.label)
  const bandLines = documentBandFactsFor(context.bandLines, deliverable, deliverable.label)
  const trucks = truckRefs(entries)
  // A deliverable the weekly milk drop feeds takes the DROP's numbers and
  // readings (`MilkScope`): the truck's count on every band, the drop's one
  // chilled reading, never a pro-rata share of either.
  const milkEntries = entries.filter(isMilkEntry)
  const milk = milkEntries.length > 0 ? milkScopeFor(milkEntries) : null
  const unitScale = milk ? 1 : truckUnitScale(trucks)
  const received = scaleReceivedFacts(
    truckReceivedFacts(milk ? trucks.filter((truck) => truck.weeklyMilk) : trucks),
    unitScale,
  )
  const temps = itemTempPrefills(
    entries,
    facts,
    context.kitchenName,
    deliverable.temperatureClass,
    milk ? milkEntries[0]!.group.group : null,
  )
  const servingTemp = servingPrefill(deliverable, facts, context.kitchenName, trucks)
  const itemKey = deliverable.deliverableKey
  const tempScopeKey = `${stepKey}::${itemKey}`
  const bands = deliverable.gradeRows
  const placement = placementFor(deliverable, context)
  const label = catalogLabelFor(deliverable, context)

  const base = (
    row: Omit<
      RecordRow,
      | 'itemKey'
      | 'temperatureClass'
      | 'tempScopeKey'
      | 'tempStoreKey'
      | 'trucks'
      | 'milk'
      | 'kit'
      | 'section'
      | 'sortRank'
    >,
  ): RecordRow => ({
    ...row,
    ...(unitScale !== 1 ? { truckUnitScale: unitScale } : {}),
    itemKey,
    temperatureClass: deliverable.temperatureClass,
    tempScopeKey,
    tempStoreKey: itemKey,
    trucks,
    milk,
    kit: placement.kit,
    section: placement.section,
    sortRank: placement.sortRank,
    ...(deliverable.fieldTripMeal ? { fieldTripMeal: true } : {}),
  })
  // The band is the GROUP header on the input, never legend text (the paper
  // splits grade groups the same way); the legend keeps the portion, the kit
  // note, and the weekly milk token.
  const legend = (): string => {
    const portion = placement.kit ? kitRowLegend(placement.kit.components.length) : (deliverable.portionLabel ?? '')
    return milk ? [portion, WEEKLY_MILK_LEGEND].filter(Boolean).join(' · ') : portion
  }

  const planned = bands.map((band) => band.planned)
  const preparedShares = facts.prepared === null ? null : apportionByPlanned(facts.prepared, planned)
  const receivedShares = received.value === null ? null : apportionByPlanned(received.value, planned)
  const countedShares = received.counted === null ? null : apportionByPlanned(received.counted, planned)

  /** The three transport prefills of one band, or of the band-less row (`gradeRow` null). */
  const transportPrefill = (
    gradeRow: ProductionFillGradeRow | null,
    i: number,
  ): Pick<RecordRowPrefill, 'ordered' | 'prepared' | 'received'> => {
    if (milk) {
      return weeklyMilkPrefill(milkEntries, received, gradeRow)
    }
    if (gradeRow === null) {
      // No band: Ordered has no order to read.
      return {
        ordered: EMPTY_VALUE,
        prepared:
          facts.prepared === null
            ? EMPTY_VALUE
            : { value: facts.prepared, tip: sourceTip(`From the delivery plan: ${facts.prepared}`) },
        received: receivedPrefill(received, received.value, received.counted),
      }
    }
    const bandFacts = bandLineTotals(bandLines, gradeLegend(gradeRow))
    const storedOrdered = gradeRow.ordered ?? null
    const storedPrepared = gradeRow.prepared ?? null
    // A milk row's stored Received is the meal's Started with, which rides on
    // the row's own edits (`isMilkStartRow`); its prefill stays the TRUCK's
    // count, so the receipt and the Started with default read the delivery.
    const storedReceived = deliverable.isMilk === true ? null : (gradeRow.received ?? null)
    const preparedShare = bandFacts?.prepared ?? preparedShares?.[i] ?? null
    const receivedShare =
      received.kind === 'confirmed'
        ? (bandFacts?.delivered ?? receivedShares?.[i] ?? null)
        : (receivedShares?.[i] ?? null)
    return {
      ordered:
        storedOrdered !== null
          ? { value: storedOrdered, tip: savedTip(storedOrdered) }
          : { value: gradeRow.planned, tip: sourceTip(`From the order: ${gradeRow.planned}`) },
      prepared:
        storedPrepared !== null
          ? { value: storedPrepared, tip: savedTip(storedPrepared) }
          : preparedShare === null
            ? EMPTY_VALUE
            : { value: preparedShare, tip: sourceTip(`From the delivery plan: ${preparedShare}`) },
      received:
        storedReceived !== null
          ? { value: storedReceived, tip: savedTip(storedReceived) }
          : receivedPrefill(received, receivedShare, countedShares?.[i] ?? null),
    }
  }

  if (bands.length === 0) {
    // The fill sent no band: the serving reading still saves, the counts have
    // no bucket.
    return [
      base({
        key: `${itemKey}::item`,
        label,
        legend: legend(),
        band: null,
        service: {
          productionRecordId: mealtime.productionRecordId,
          deliverableKey: deliverable.deliverableKey,
          deliverable,
          gradeRow: null,
          rowKey: null,
          planned: plannedTotalOf(entries) ?? 0,
        },
        fallbackIdentity: null,
        prefill: {
          ...transportPrefill(null, 0),
          served: EMPTY_VALUE,
          reimbursable: EMPTY_VALUE,
          nonReimbursable: EMPTY_VALUE,
          leftover: EMPTY_VALUE,
          ...temps,
          servingTemp,
        },
      }),
    ]
  }

  return bands.map((gradeRow, i) => {
    const band = gradeLegend(gradeRow)
    const rowKey = productionFillRowKey(deliverable.deliverableKey, gradeRow)
    return base({
      key: rowKey,
      label,
      legend: legend(),
      band,
      service: {
        productionRecordId: mealtime.productionRecordId,
        deliverableKey: deliverable.deliverableKey,
        deliverable,
        gradeRow,
        rowKey,
        planned: gradeRow.planned,
      },
      fallbackIdentity: null,
      prefill: {
        ...transportPrefill(gradeRow, i),
        served: { value: gradeRow.served, tip: null },
        reimbursable: { value: gradeRow.reimbursable, tip: null },
        nonReimbursable: { value: gradeRow.nonReimbursable, tip: null },
        leftover: { value: gradeRow.leftover, tip: null },
        ...temps,
        servingTemp,
      },
    })
  })
}

/**
 * Truck lines no record deliverable claimed — a receipt the school still owes.
 * ONE flat row per ITEM (label + class), however many trucks brought it. A
 * weekly milk SKU nobody recognised is one of these too, legend
 * `Weekly milk delivery · not on this record` on a record step (`onRecord`),
 * its counts and readings still scoped to the SKU and the drop.
 */
function transportOnlyRows(
  stepKey: string,
  entries: readonly TransportEntry[],
  context: RecordContext,
  onRecord: boolean,
): RecordRow[] {
  const grouped = new Map<string, TransportEntry[]>()
  for (const entry of entries) {
    const key = nameClassKey(entry.row.label, entry.row.temperatureClass)
    grouped.set(key, [...(grouped.get(key) ?? []), entry])
  }
  const rows: RecordRow[] = []
  for (const [key, group] of grouped) {
    const first = group[0]!
    const parts = group.flatMap((entry) => entry.row.parts)
    const componentKeys = parts.flatMap((part) => (part.componentId ? [`co:${part.componentId}`] : []))
    const facts = documentFactsFor(context.index, null, first.row.label, componentKeys)
    const node = parts
      .map((part) => (part.componentId ? context.componentNodes.get(part.componentId) : undefined))
      .find(Boolean)
    const fallbackIdentity =
      node && (node.bundleDeliverableId || node.menuItemId)
        ? { bundleDeliverableId: node.bundleDeliverableId, menuItemId: node.menuItemId }
        : null
    const milkEntries = group.filter(isMilkEntry)
    const milk = milkEntries.length > 0 ? milkScopeFor(milkEntries) : null
    const trucks = truckRefs(group)
    const received = truckReceivedFacts(trucks)
    const plannedTotal = plannedTotalOf(group)
    const itemKey = `transport::${key}`
    const kinds = parts.map((part) =>
      part.componentId ? (context.componentNodes.get(part.componentId)?.componentKind ?? null) : null,
    )
    rows.push({
      key: `${itemKey}::item`,
      itemKey,
      label: first.row.label,
      legend: milk
        ? milkLegend(milkEntries[0]!, onRecord && fallbackIdentity === null)
        : (transportRowLegend(first.row) ?? first.row.unitLabel),
      band: null,
      temperatureClass: first.row.temperatureClass,
      tempScopeKey: `${stepKey}::${itemKey}`,
      tempStoreKey: itemKey,
      service: null,
      trucks,
      fallbackIdentity,
      milk,
      kit: null,
      section: sectionOfKinds(kinds),
      sortRank: rowKindRank(kinds),
      prefill: {
        ...(milk
          ? weeklyMilkPrefill(milkEntries, received, null)
          : {
              ordered:
                plannedTotal === null
                  ? EMPTY_VALUE
                  : { value: plannedTotal, tip: sourceTip(`From the delivery plan: ${plannedTotal}`) },
              prepared:
                facts.prepared === null
                  ? EMPTY_VALUE
                  : { value: facts.prepared, tip: sourceTip(`From the delivery plan: ${facts.prepared}`) },
              received: receivedPrefill(received, received.value, received.counted),
            }),
        served: EMPTY_VALUE,
        reimbursable: EMPTY_VALUE,
        nonReimbursable: EMPTY_VALUE,
        leftover: EMPTY_VALUE,
        ...itemTempPrefills(
          group,
          facts,
          context.kitchenName,
          first.row.temperatureClass,
          milk ? milkEntries[0]!.group.group : null,
        ),
        servingTemp: NO_SERVING(first.row.temperatureClass),
      },
    })
  }
  return rows
}

/** The shipments a step's rows stand on, summarised from the input or from the rows' own truck facts. */
function stepTrucks(rows: readonly RecordRow[], shipmentsById: ReadonlyMap<string, TruckSummary>): TruckSummary[] {
  const seen = new Map<string, TruckSummary>()
  for (const row of rows) {
    for (const truck of row.trucks) {
      if (seen.has(truck.shipmentId)) {
        continue
      }
      seen.set(
        truck.shipmentId,
        shipmentsById.get(truck.shipmentId) ?? {
          shipmentId: truck.shipmentId,
          deliveryDate: truck.deliveryDate,
          status: truck.pending ? 'NEEDS_RECEIPT' : 'RECEIVED',
          receivedAt: truck.receivedAt,
          receiverName: truck.receiverName,
        },
      )
    }
  }
  return [...seen.values()].sort((a, b) => a.deliveryDate.localeCompare(b.deliveryDate))
}

function makeStep(
  step: Pick<RecordStep, 'key' | 'mealtime' | 'title' | 'productionRecordId' | 'status' | 'rows' | 'fill'>,
  doc: ProductionRecordDocument | null,
  shipmentsById: ReadonlyMap<string, TruckSummary>,
): RecordStep {
  const kitchen = doc ? kitchenSignature(doc) : { name: null, signedAt: null }
  return {
    ...step,
    signed: isSignedStatus(step.status),
    trucks: stepTrucks(step.rows, shipmentsById),
    kitchen: { signatureName: kitchen.name, signedAt: kitchen.signedAt },
    signature: doc
      ? { name: doc.service.signature.name, signedAt: doc.service.signature.signedAt }
      : { name: null, signedAt: null },
  }
}

export function buildRecordSteps(input: SpreadsheetInput): RecordStep[] {
  const shipmentsById = new Map((input.shipments ?? []).map((s) => [s.shipmentId, s] as const))
  const contexts: RecordContext[] = input.mealtimes.map((mealtime) =>
    recordContext(mealtime, input.documents.get(mealtime.productionRecordId) ?? null),
  )

  // Every truck line of a mealtime, and which records serve it. A campus with
  // TWO vendors serving one lunch has TWO lunch records, and a per-record pass
  // over the same trucks would show the same Received number on both.
  const entriesByMealtime = new Map<Mealtime, TransportEntry[]>()
  for (const group of input.transport) {
    const list = entriesByMealtime.get(group.mealtime) ?? []
    for (const row of group.rows) {
      list.push({ group, row })
    }
    entriesByMealtime.set(group.mealtime, list)
  }
  const recordsByMealtime = new Map<Mealtime, number[]>()
  input.mealtimes.forEach((mealtime, i) => {
    recordsByMealtime.set(mealtime.mealtime, [...(recordsByMealtime.get(mealtime.mealtime) ?? []), i])
  })

  // CLAIM ONCE, per mealtime. The join runs TRUCK ROW → DELIVERABLE: a line
  // goes to the FIRST record that both may have it (vendor) and recognises it
  // (the three-step match); the rest become that record's transport-only rows.
  const claims = new Map<number, Map<number, TransportEntry[]>>()
  const orphansByRecord = new Map<number, TransportEntry[]>()
  for (const [mealtime, entries] of entriesByMealtime) {
    const recordIndices = recordsByMealtime.get(mealtime) ?? []
    if (recordIndices.length === 0) {
      continue
    }
    for (const entry of entries) {
      let claimed = false
      for (const recordIndex of recordIndices) {
        const context = contexts[recordIndex]!
        if (!vendorAllows(context, entry.row)) {
          continue
        }
        const hit = matchDeliverableIndex(entry.row, context.lookup, context.componentIdentities)
        if (hit === null) {
          continue
        }
        const byDeliverable = claims.get(recordIndex) ?? new Map<number, TransportEntry[]>()
        byDeliverable.set(hit, [...(byDeliverable.get(hit) ?? []), entry])
        claims.set(recordIndex, byDeliverable)
        claimed = true
        break
      }
      if (claimed) {
        continue
      }
      // Nobody recognised it. It still goes on exactly ONE record — the one
      // whose vendor it matches, else the mealtime's first — because the same
      // receipt rendered on two records is the same truck offered twice.
      const owner =
        recordIndices.find((i) => contexts[i]!.vendorKey !== null && vendorAllows(contexts[i]!, entry.row)) ??
        recordIndices[0]!
      orphansByRecord.set(owner, [...(orphansByRecord.get(owner) ?? []), entry])
    }
  }

  // WEEKLY MILK joins EVERY record of the day, not claim-once: the drop is one
  // stack of cartons for the whole day, and the SKU that is the breakfast
  // record's `1% Milk` is the lunch record's too. No vendor gate either — the
  // dairy is never the record's kitchen. A SKU no record recognises stays a
  // transport-only row (see below).
  const milkOrphans: TransportEntry[] = []
  for (const group of input.milk) {
    for (const row of group.group.rows) {
      const entry: TransportEntry = { group, row }
      let joined = false
      contexts.forEach((context, recordIndex) => {
        const hit = matchDeliverableIndex(row, context.lookup, context.componentIdentities)
        if (hit === null) {
          return
        }
        const byDeliverable = claims.get(recordIndex) ?? new Map<number, TransportEntry[]>()
        byDeliverable.set(hit, [...(byDeliverable.get(hit) ?? []), entry])
        claims.set(recordIndex, byDeliverable)
        joined = true
      })
      if (!joined) {
        milkOrphans.push(entry)
      }
    }
  }

  const steps: RecordStep[] = []
  input.mealtimes.forEach((mealtime, recordIndex) => {
    const context = contexts[recordIndex]!
    const stepKey = mealtime.productionRecordId
    const claimed = claims.get(recordIndex) ?? new Map<number, TransportEntry[]>()
    const rows: RecordRow[] = []
    mealtime.deliverables.forEach((deliverable, i) => {
      rows.push(...deliverableRows(stepKey, mealtime, deliverable, claimed.get(i) ?? [], context))
    })
    rows.push(...transportOnlyRows(stepKey, orphansByRecord.get(recordIndex) ?? [], context, true))
    orderRowsLikeThePaper(rows)
    steps.push(
      makeStep(
        {
          key: stepKey,
          mealtime: mealtime.mealtime,
          title: MEALTIME_LABELS[mealtime.mealtime],
          productionRecordId: mealtime.productionRecordId,
          status: mealtime.status,
          rows,
          fill: mealtime,
        },
        context.doc,
        shipmentsById,
      ),
    )
  })

  // A mealtime whose trucks have NO record at all — a SNACK drop on a day whose
  // fill holds only breakfast and lunch, or a whole day the generator has not
  // reached. Those receipts are still owed, so they get their own step.
  for (const [mealtime, entries] of entriesByMealtime) {
    if ((recordsByMealtime.get(mealtime) ?? []).length > 0) {
      continue
    }
    const stepKey = `transport::${mealtime}`
    steps.push(
      makeStep(
        {
          key: stepKey,
          mealtime,
          title: MEALTIME_LABELS[mealtime],
          productionRecordId: null,
          status: null,
          rows: transportOnlyRows(stepKey, entries, emptyContext(), false),
          fill: null,
        },
        null,
        shipmentsById,
      ),
    )
  }

  // A weekly milk SKU no record recognised is still a receipt the school owes.
  // It rides on the first UNSIGNED step (a signed step locks its controls, and
  // the drop must stay receivable); a day with nothing else gets a step of its
  // own for it. Record steps precede the record-less ones in `steps`, so a
  // host's index below `contexts.length` is its record's context.
  if (milkOrphans.length > 0) {
    const host = steps.find((step) => !step.signed) ?? steps[0]
    if (host) {
      const hostIndex = steps.indexOf(host)
      const context = hostIndex < contexts.length ? contexts[hostIndex]! : emptyContext()
      host.rows.push(...transportOnlyRows(host.key, milkOrphans, context, host.productionRecordId !== null))
      host.trucks = stepTrucks(host.rows, shipmentsById)
    } else {
      const stepKey = 'transport::milk'
      steps.push(
        makeStep(
          {
            key: stepKey,
            mealtime: null,
            title: 'Milk delivery',
            productionRecordId: null,
            status: null,
            rows: transportOnlyRows(stepKey, milkOrphans, emptyContext(), false),
            fill: null,
          },
          null,
          shipmentsById,
        ),
      )
    }
  }

  return steps
}

// ---------------------------------------------------------------------------
// Cells: what a row offers, and the step's progress
// ---------------------------------------------------------------------------

/**
 * Order a step's rows the way the paper prints them (PROD-1441): grade group
 * (bands in first-seen order, band-less rows last), then main choices /
 * shared sides and milk / condiments, then the kind order, then the label.
 * Stable, in place.
 */
function orderRowsLikeThePaper(rows: RecordRow[]): void {
  const bandOrder = new Map<string, number>()
  for (const row of rows) {
    if (row.band !== null && !bandOrder.has(row.band)) {
      bandOrder.set(row.band, bandOrder.size)
    }
  }
  const bandRank = (row: RecordRow): number =>
    row.band === null ? bandOrder.size : (bandOrder.get(row.band) ?? bandOrder.size)
  const sectionRank = (row: RecordRow): number => RECORD_ROW_SECTIONS.indexOf(row.section)
  rows.sort(
    (a, b) =>
      bandRank(a) - bandRank(b) ||
      sectionRank(a) - sectionRank(b) ||
      a.sortRank - b.sortRank ||
      a.label.localeCompare(b.label, undefined, { numeric: true }),
  )
}

/** One section of a grade group on the input: its title and the rows (counting rows; kit recipes ride on them). */
export interface RecordRowSectionGroup {
  key: RecordRowSection
  title: string
  rows: RecordRow[]
}

/** One grade-group block of a step on the input, as the paper prints it. */
export interface RecordRowGroup {
  key: string
  /** The band, or `ALL_GRADES_GROUP` for rows the record never banded. */
  band: string
  sections: RecordRowSectionGroup[]
}

/**
 * The step's rows as grade-group blocks → sections, in the order
 * `buildRecordSteps` already put them in. Pure: the table, the phone cards
 * and the claim total all read this one grouping.
 *
 * Unbanded rows (a weekly milk drop, a whole-school delivery) join the step's
 * band when it has exactly ONE: a K-8 campus is one grade group, and a second
 * `All grades` block under it reads as a duplicate record (Amana Academy,
 * 2026-11-02). With several bands there is no single owner, so they keep
 * their own `All grades` block.
 */
export function groupRecordRows(rows: readonly RecordRow[]): RecordRowGroup[] {
  const bands = new Set(rows.flatMap((row) => (row.band ? [row.band] : [])))
  const soleBand = bands.size === 1 ? ([...bands][0] as string) : null
  const groups: RecordRowGroup[] = []
  for (const row of rows) {
    const band = row.band ?? soleBand ?? ALL_GRADES_GROUP
    let group = groups.find((g) => g.band === band)
    if (!group) {
      group = { key: band, band, sections: [] }
      groups.push(group)
    }
    let section = group.sections.find((sec) => sec.key === row.section)
    if (!section) {
      section = { key: row.section, title: RECORD_ROW_SECTION_TITLE[row.section], rows: [] }
      group.sections.push(section)
    }
    section.rows.push(row)
  }
  return groups
}

/**
 * The meals a record row's counts belong to: the menu items its deliverable was
 * ordered as (`mi:<id>`). Cereal and string cheese of one breakfast bundle
 * share the bundle's menu item, so they are one meal (`claimedMeals`).
 */
export function recordRowMealKeys(row: Pick<RecordRow, 'service'>): string[] {
  const ids = (row.service?.deliverable.lineTargets ?? [])
    .map((target) => target.menuItemId)
    .filter((id): id is string => id !== null)
  return [...new Set(ids)].map((id) => `mi:${id}`)
}

/**
 * Whether a row's column takes input at all. The three transport counts
 * always do; the four service counts and the serving reading only where they
 * have somewhere to save (a record band, or a document identity on a
 * transport-only row); a shelf-stable row takes no reading.
 */
export function cellOffered(row: RecordRow, column: RecordColumn): boolean {
  switch (column) {
    case 'ordered':
    case 'prepared':
    case 'received':
      return true
    case 'served':
    case 'reimbursable':
    case 'nonReimbursable':
    case 'leftover':
      return row.service?.gradeRow != null || row.fallbackIdentity !== null
    case 'kitchenTemp':
    case 'receivedTemp':
      return tempCheckRequired(row.temperatureClass)
    case 'servingTemp':
      return tempCheckRequired(row.temperatureClass) && (row.service !== null || row.fallbackIdentity !== null)
  }
}

/**
 * The rail's `filled / total`: every offered cell of the step, temperature
 * cells counted ONCE per scope, filled when its displayed value is non-empty
 * (a prefill counts, as on the chef page).
 */
export function stepProgress(
  step: RecordStep,
  valueOf: (row: RecordRow, column: RecordColumn) => number | null,
): { filled: number; total: number } {
  let filled = 0
  let total = 0
  const scopesCounted = new Set<string>()
  for (const row of step.rows) {
    for (const column of RECORD_COLUMNS) {
      if (!cellOffered(row, column)) {
        continue
      }
      if (isTempColumn(column)) {
        const id = tempCellIdFor(row, column)
        if (scopesCounted.has(id)) {
          continue
        }
        scopesCounted.add(id)
      }
      total += 1
      if (valueOf(row, column) !== null) {
        filled += 1
      }
    }
  }
  return { filled, total }
}

// ---------------------------------------------------------------------------
// Scan: request rows + write-back
// ---------------------------------------------------------------------------

/** Where one scanned row writes back to: the flat row itself. */
export interface RecordScanTarget {
  stepKey: string
  row: RecordRow
}

/** The scan wire id of a row: unique across the whole day. */
export function recordScanRowId(stepKey: string, row: RecordRow): string {
  return `${stepKey}::${row.key}`
}

/**
 * The `rows` half of a `task: 'record'` scan request for ONE step, plus the
 * index that routes the answer back. FLAT (B7): every row is top-level, the
 * band is the `groupLabel`, `parentRowId` is always null.
 */
export function buildRecordScanRows(step: RecordStep): {
  rows: ScanRowWire[]
  index: Map<string, RecordScanTarget>
} {
  const rows: ScanRowWire[] = []
  const index = new Map<string, RecordScanTarget>()
  for (const row of step.rows) {
    const rowId = recordScanRowId(step.key, row)
    // A weekly milk row's sheet number is the drop's count, not the band's
    // order, so a milk row never reads its band's planned figure.
    const bandRow = row.milk ? null : (row.service?.gradeRow ?? null)
    rows.push({
      rowId,
      name: row.label,
      // The band leads the group label: the paper prints the row inside its
      // grade-group block, and the reader matches rows by item and band.
      groupLabel: [row.band, row.legend].filter((part) => part !== null && part !== '').join(' · '),
      mealtimeLabel: step.title,
      temperatureClass: lowerTemperatureClass(row.temperatureClass),
      planned:
        bandRow !== null
          ? bandRow.planned
          : row.trucks.length > 0
            ? row.trucks.reduce((s, t) => s + t.plannedQuantity, 0)
            : null,
      parentRowId: null,
    })
    index.set(rowId, { stepKey: step.key, row })
  }
  return { rows, index }
}

function lowerTemperatureClass(value: TemperatureClass | null): ScanRowWire['temperatureClass'] {
  switch (value) {
    case 'HOT':
      return 'hot'
    case 'CHILLED':
      return 'chilled'
    case 'AMBIENT':
      return 'ambient'
    default:
      return null
  }
}

/** One scanned Received count, on its band row. */
export interface ReceivedScanPatch {
  stepKey: string
  row: RecordRow
  value: number
}

/** One scanned temperature, on its SCOPE (every row of the item follows). */
export interface TempScanPatch {
  stepKey: string
  /** `tempScopeKeyFor(row, column)`: the item's, or the drop's for a weekly milk row's Received °F. */
  scopeKey: string
  /** `tempStoreKeyFor(row, column)`: the full `edits[step].temps` key. */
  storeKey: string
  column: 'receivedTemp' | 'servingTemp'
  value: number
  confidence: 'high' | 'low'
  /** The first row that carried the reading, for the receipt's line lookup. */
  row: RecordRow
}

/** One scanned field-trip count, on its milk band row. */
export interface FieldTripScanPatch {
  stepKey: string
  row: RecordRow
  value: number
}

export interface RecordScanApplication {
  received: ReceivedScanPatch[]
  /** The milk rows' "Sent on field trip" counts; applied before `counts`, whose `planned` already nets them out. */
  fieldTrips: FieldTripScanPatch[]
  /** The four counts, for rows with a record band (the flow re-derives per row). */
  counts: ServiceCountPatch[]
  temps: TempScanPatch[]
  /** `rowKey` is a `countCellId` prefix (`${stepKey}::${rowKey}`) or a scope key; `field` the column. */
  flags: ScanCellFlag[]
  summary: ScanPartialSummary
}

const SCAN_TEMP_COLUMNS: ReadonlyArray<{ wire: string; column: 'receivedTemp' | 'servingTemp' }> = [
  { wire: 'dropOffTemperatureF', column: 'receivedTemp' },
  { wire: 'servingTemperatureF', column: 'servingTemp' },
]

/**
 * Route a `task: 'record'` response onto the flat rows.
 *
 * PATCHES, never merged maps: a scan runs for up to two minutes with the table
 * live, so the flow merges these over the LATEST state. A null cell is simply
 * absent, which IS the "a null keeps what is there" rule. A Received count lands
 * on the band row it was read from; a temperature lands on the row's SCOPE, the
 * first non-null reading per scope wins, and a second row of the same item
 * reading a DIFFERENT number flags the scope amber and says so.
 */
export function applyRecordScan(
  result: ScanSheetResponse,
  index: ReadonlyMap<string, RecordScanTarget>,
  current: (row: RecordRow, column: RecordColumn) => number | null,
  /** The row's field-trip count on screen (0 when none); a scan's own count wins. */
  currentFieldTrip: (row: RecordRow) => number = () => 0,
): RecordScanApplication {
  const received: ReceivedScanPatch[] = []
  const fieldTrips: FieldTripScanPatch[] = []
  const counts: ServiceCountPatch[] = []
  const temps: TempScanPatch[] = []
  const flags: ScanCellFlag[] = []
  const warnings: string[] = []
  const tempsByScope = new Map<string, TempScanPatch>()
  const disagreed = new Set<string>()
  let filledCount = 0
  let lowConfidenceCount = 0

  for (const scanned of result.rows) {
    const target = index.get(scanned.rowId)
    if (!target) {
      continue
    }
    const { stepKey, row } = target

    const receivedCell = scanned.cells['receivedQuantity']
    if (receivedCell && receivedCell.value !== null) {
      received.push({ stepKey, row, value: receivedCell.value })
      filledCount += 1
      if (receivedCell.confidence === 'low') {
        lowConfidenceCount += 1
        // `${rowKey}::${field}` is the cell id: the SKU scope's on a weekly milk
        // transport-only row, the row's own where Received is the meal's start.
        flags.push({
          rowKey: row.milk && !isMilkStartRow(row) ? row.milk.scopeKey : `${stepKey}::${row.key}`,
          field: 'received',
        })
      }
    }

    // A milk row's "Sent on field trip" box: the cartons that left with the trip.
    const fieldTripCell = hasFieldTripLine(row) ? scanned.cells['fieldTrip'] : undefined
    if (fieldTripCell && fieldTripCell.value !== null) {
      filledCount += 1
      if (fieldTripCell.confidence === 'low') {
        lowConfidenceCount += 1
        flags.push({ rowKey: `${stepKey}::${row.key}`, field: 'fieldTrip' })
      }
      if (fieldTripCell.value !== currentFieldTrip(row)) {
        fieldTrips.push({ stepKey, row, value: fieldTripCell.value })
      }
    }
    const fieldTrip = hasFieldTripLine(row) ? (fieldTripCell?.value ?? currentFieldTrip(row)) : 0

    if (row.service?.gradeRow && row.service.rowKey) {
      for (const field of SERVICE_COUNT_COLUMNS) {
        const cell = scanned.cells[field]
        if (!cell || cell.value === null) {
          continue
        }
        filledCount += 1
        if (cell.confidence === 'low') {
          lowConfidenceCount += 1
          flags.push({ rowKey: `${stepKey}::${row.key}`, field })
        }
        if (current(row, field) === cell.value) {
          continue
        }
        counts.push({
          prId: row.service.productionRecordId,
          rowKey: row.service.rowKey,
          field,
          value: cell.value,
          // Every band row's counts interlock against what the meal had on
          // hand, its Prepared (the scanned one when the sheet has one).
          // Milk that left with the trip is neither served nor left over.
          planned: onHandAfterFieldTrip(
            receivedCell?.value ?? current(row, 'received') ?? row.service.planned,
            fieldTrip,
          ),
        })
      }
    }

    for (const { wire, column } of SCAN_TEMP_COLUMNS) {
      const cell = scanned.cells[wire]
      if (!cell || cell.value === null || !cellOffered(row, column)) {
        continue
      }
      const scopeKey = tempScopeKeyFor(row, column)
      const scopeId = tempCellId(scopeKey, column)
      const existing = tempsByScope.get(scopeId)
      if (!existing) {
        const patch: TempScanPatch = {
          stepKey,
          scopeKey,
          storeKey: tempStoreKeyFor(row, column),
          column,
          value: cell.value,
          confidence: cell.confidence,
          row,
        }
        tempsByScope.set(scopeId, patch)
        temps.push(patch)
        filledCount += 1
        if (cell.confidence === 'low') {
          lowConfidenceCount += 1
          flags.push({ rowKey: scopeKey, field: column })
        }
        continue
      }
      if (existing.value !== cell.value && !disagreed.has(scopeId)) {
        disagreed.add(scopeId)
        flags.push({ rowKey: scopeKey, field: column })
        warnings.push(`Two rows of ${row.label} show different temperatures on the sheet. Check the value.`)
      }
    }
  }

  return {
    received,
    fieldTrips,
    counts,
    temps,
    flags,
    summary: {
      filledCount,
      lowConfidenceCount,
      warnings: [...buildPhotoWarnings(result.images), ...warnings],
      unmatchedRowsSeen: result.unmatchedRowsSeen,
    },
  }
}

/**
 * Cells a photo could fill on this step — the denominator in the card's
 * "Filled X of Y fields": Received on every row, the four counts on every
 * record band, and the two scannable readings once per scope. Ordered,
 * Prepared and Kitchen °F are not on the extractor's column set (ASSUMED-9).
 */
export function recordScanFieldTotal(index: ReadonlyMap<string, RecordScanTarget>): number {
  let total = 0
  const scopes = new Set<string>()
  for (const { row } of index.values()) {
    total += 1
    if (row.service?.gradeRow) {
      total += SERVICE_COUNT_COLUMNS.length
    }
    for (const { column } of SCAN_TEMP_COLUMNS) {
      const id = tempCellIdFor(row, column)
      if (cellOffered(row, column) && !scopes.has(id)) {
        scopes.add(id)
        total += 1
      }
    }
  }
  return total
}
